import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { Button, Checkbox, Collapse, DatePicker, Drawer, Flex, Form, Input, Modal, notification, Select, Spin, Switch, Upload } from "antd";
import PhoneNumberInput from "../../components/shared/PhoneNumberInput";
import Icon from "@mdi/react";
import {
  mdiCalendarMonth,
  mdiCheck,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
  mdiClose,
  mdiEmail,
  mdiFileUploadOutline,
  mdiInformationOutline,
  mdiLightningBolt,
  mdiMagnify,
  mdiMinus,
  mdiNavigation,
  mdiPhone,
  mdiPlus,
  mdiWhatsapp,
} from "@mdi/js";
import { format, parse } from "date-fns";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Itinerary, Tour } from "../../interfaces/itinerary.interface";
import { BookingInput } from "../../interfaces/booking.interface";
import { PromotionDeduction } from "../../interfaces/promotion.interface";
import { PaxPriceItem } from "../../utils/promotions";
import { dateRibbonTag } from "../../utils/tagDisplay";
import { Agent } from "../../interfaces/agent.interface";
import TourDateSelectorV3 from "../../components/shared/v3/TourDateSelector";
import FlightScheduleV3 from "../../components/shared/v3/FlightScheduleV3";
import PriceChartV3 from "../../components/shared/v3/PriceChartV3";
import PromoBannerV3 from "../../components/shared/v3/PromoBannerV3";
import PromotionTagsV3 from "../../components/shared/v3/PromotionTagsV3";
import { PAYMENT_TERMS, pricingCategory } from "../../enums/pricing.enum";
import { categoryDobDisabledDate } from "../../utils/antd.props";
import { DTA_TYPE } from "../../enums/dta.enum";
import { addons, childAgeLabel, CRUISE_CATEGORY, currencyDisplay, getPriceTierForPassenger, getPricingTierRow, groupFareTiers, parsePricingTiers, priceTypeLabel, validateBooking, withMalaysiaFirst } from "../../utils/helpers";
import { b2c2bUploadProps, passportExpiryDisabledDates } from "../../utils/antd.props";
import ListApi from "../../api-services/list-api.service";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import CustomerApi from "../../api-services/customer-api.service";
import SeriesApi from "../../api-services/series-api.service";
import { useAuth } from "../../services/AuthContext";
import TermsConsentCheckBox from "./b2c2b/TermsConsentCheckBox";
import PrivacyConsentCheckbox from "./b2c2b/PrivacyConsentCheckbox";
import SignInForm from "./SignInForm";
import CruiseCabinSelectorV3 from "../../components/shared/v3/CruiseCabinSelector";
import CruiseTravelersV3 from "../../components/shared/v3/CruiseTravelers";
import { CruiseCabin, CruiseDeparture, CruiseRoomType } from "../../interfaces/itinerary.interface";
import {
  CruiseCabinPax,
  cabinMetaFromGroups,
  cruisePaxSummary,
  findCabinDetail,
  flattenCabinsToTravelers,
  maxCabinsFor,
  totalCruisePax,
  travelerCountsFrom,
} from "../../utils/cruise";

dayjs.extend(customParseFormat);

interface BookingFormV3Props {
  itinerary: Itinerary;
  selectedTour: Tour;
  agents: Agent[];
  agentsLoading?: boolean;
  paymentMethods: Record<string, any> | null;
  open?: boolean;
  isLoading?: boolean;
  selectTour?: (params: { date: string; code: string; id?: number }) => void;
  onClose: () => void;
  // Promotion calculation is owned by the parent (ShowPage): it calls the
  // calculate endpoint and persists the response; this form only reports its
  // pax selection up and renders the results passed back down.
  promotionDeductions?: PromotionDeduction[];
  promotionTotal?: number;
  promotionCalcLoading?: boolean;
  onPaxItemsChange?: (items: PaxPriceItem[], tourId?: number) => void;

  isCruise?: boolean;
  cruiseDeparture?: CruiseDeparture;
  cruiseMultiplier?: number;
  cruiseCabinType?: string;
  cruiseTourId?: number;
  cruiseCabins?: CruiseCabinPax[];
  setCruiseCabins?: React.Dispatch<React.SetStateAction<CruiseCabinPax[]>>;
  cruiseCabinGroups?: number[];
  onSelectCruiseRoomType?: (roomType: CruiseRoomType) => void;
  onSelectCruiseCabin?: (cabin: CruiseCabin) => void;
}

interface LocalInfant {
  given_name: string;
  surname: string;
  date_of_birth: string;
  gender: string;
}

interface LocalPassenger {
  given_name: string;
  surname: string;
  phone: string;
  email: string;
  category: string;
  infants: LocalInfant[];
  designation?: string;
  nationality?: string;
  date_of_birth?: string;
  nric?: string;
  passport_number?: string;
  passport_expiry?: string;
  passport_photocopy?: any;
  customer_remark?: string;
  same_as_pax1?: boolean;
}

const makePassenger = (cat: string): LocalPassenger => ({
  given_name: "", surname: "", phone: "", email: "", category: cat, infants: [],
});

interface StickyReferralBinding {
  id?: string | number;
  source?: string;
  email?: string;
  referralEntityId?: number | string;
  referralEntityType?: string;
  referralEntityEmail?: string;
  referralEntityName?: string;
  agent?: Partial<Agent>;
  agency?: any;
  subagent?: Partial<Agent>;
  pendingStickyChangeRequest?: {
    id?: string | number;
    status?: string;
    expires_at?: string;
    message?: string;
  } | null;
}

interface ReferralAgentChange {
  referral_agent_id: number;
  referral_link?: string;
  change_reason: string;
  other_reason?: string;
}

interface AgentSelectionHistory {
  type: "blank" | "self_select" | "referral_link";
  selectedAgentId: number | null;
  preferredAgent: any;
  dtaType: string;
  changeAgent: ReferralAgentChange | null;
}

const DESIGNATIONS = [
  { value: "Mr", label: "Mr" },
  { value: "Ms", label: "Ms" },
  { value: "Miss", label: "Miss" },
  { value: "Mstr", label: "Mstr" },
];

const SectionHeader = ({ num, title }: { num: number; title: string }) => (
  <div className="drawer-section-header">
    <span className="drawer-section-header_num">{num}</span>
    <span className="drawer-section-header_title">{title}</span>
  </div>
);

const STEPS = [
  { step: 1, title: "Please choose your departure date" },
  { step: 2, title: "Please fill up traveller details" },
  { step: 3, title: "Please choose your preferred partner" },
  { step: 4, title: "" },
] as const;

const TOTAL_STEPS = STEPS.length;

const PAYMENT_CHANNELS = [
  { id: 1, value: "dobw",         logo: "duitnow.png",  label: "Online Banking/Wallets", bankName: "dobw_banklist", fieldLabel: "Bank" },
  { id: 2, value: "public_bank_2",logo: "master.png",   label: "Credit/Debit Card",      bankName: "pbb_banklist",  fieldLabel: "Card Method" },
  { id: 3, value: "fpx_b2c",      logo: "fpx-new.png",  label: "B2C (Retail Banking)",   bankName: "banklist",      fieldLabel: "Bank" },
];

const safeFormat = (date: string) => {
  try { return format(parse(date, "dd/MM/yyyy", new Date()), "dd/MM/yyyy"); }
  catch { return date; }
};

const bookingScroller = () =>
  document.querySelector(".booking-drawer .ant-drawer-body") as HTMLElement | null;

const DobDatePicker: React.FC<React.ComponentProps<typeof DatePicker>> = (props) => {
  const [mode, setMode] = useState<"decade" | "year" | "month" | "date">("year");
  return (
    <DatePicker
      {...props}
      mode={mode}
      onOpenChange={open => { if (open) setMode("year"); }}
      onPanelChange={(_, newMode) => setMode(newMode as typeof mode)}
      onChange={(...args) => { setMode("year"); props.onChange?.(...args); }}
    />
  );
};

const BookingFormV3: React.FC<BookingFormV3Props> = ({
  itinerary, selectedTour, agents, agentsLoading, paymentMethods, open, isLoading, selectTour, onClose,
  promotionDeductions = [], promotionTotal = 0, promotionCalcLoading = false, onPaxItemsChange,
  isCruise = false, cruiseDeparture, cruiseMultiplier, cruiseCabinType, cruiseTourId,
  cruiseCabins = [], setCruiseCabins, cruiseCabinGroups,
  onSelectCruiseRoomType, onSelectCruiseCabin,
}) => {
  const { username } = useAuth();
  const isGuest = !username;
  const checkoutDisabled = itinerary.checkoutEnabled === false;
  const checkoutMessage = itinerary.checkoutDisabledMessage || "Only Customer can make bookings on this portal. Please log out to book as a customer.";

  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    if (!open) return;
    setCurrentStep(1);
    setTravellersConfirmed(false);
    setTravellersDrawerOpen(false);
  }, [open]);
  const [incompletePassengerIdxs, setIncompletePassengerIdxs] = useState<number[]>([]);

  const [activePassengerIdx, setActivePassengerIdx] = useState(0);
  const [tabsExpanded, setTabsExpanded] = useState(false);
  const [tabsOverflow, setTabsOverflow] = useState(false);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const tabsWrapRef = useRef<HTMLDivElement>(null);  // sticky wrapper — measured for scroll offset
  const tabsRowRef = useRef<HTMLDivElement>(null);   // horizontally scrolling row
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabScrollSettleAtRef = useRef(0);

  // Step 4 summary-card caption: clamp to 2 lines with a Read More toggle.
  const [summaryTourExpanded, setSummaryTourExpanded] = useState(false);
  const [summaryTourClamped, setSummaryTourClamped] = useState(false);
  const summaryTourRef = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    if (currentStep !== 4) return;
    setSummaryTourExpanded(false);
    const el = summaryTourRef.current;
    if (!el) { setSummaryTourClamped(false); return; }
    const cs = getComputedStyle(el);
    let lineHeight = parseFloat(cs.lineHeight);
    if (!Number.isFinite(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.4;
    setSummaryTourClamped(el.scrollHeight > lineHeight * 2 + 1);
  }, [itinerary.caption, currentStep]);

  const [localSelectedTour, setLocalSelectedTour] = useState<Tour>(selectedTour);
  const [cruiseTourDetailLoading, setCruiseTourDetailLoading] = useState(false);
  useEffect(() => { setLocalSelectedTour(selectedTour); }, [selectedTour.code]);

  const [showPriceChart, setShowPriceChart] = useState(false);
  const [showPriceSummary, setShowPriceSummary] = useState(false);

  const [travellersDrawerOpen, setTravellersDrawerOpen] = useState(false);
  const [travellersConfirmed, setTravellersConfirmed] = useState(false);

  const dates = useMemo(() => {
    if (itinerary.cabinNavigation) {
      return (itinerary.cabinNavigation.departures || []).map((departure) => ({
        id: undefined,
        code: departure.departure_date,
        date: departure.departure_date,
        price: currencyDisplay(itinerary.priceCurrency || "MYR", departure.from_price),
        type: departure.guaranteed ? "guaranteed" : "",
        location: "",
        indicator: "",
        promoTag: undefined,
      }));
    }
    if ((itinerary.tours?.length || 0) === 0) return [];
    return itinerary.tours.map((tour: any) => {
      const almostGuaranteed = tour.guaranteed_indicator?.includes("to G") ?? false;
      const type = tour.guaranteed_indicator === "G" ? "guaranteed" : "";
      const indicator = almostGuaranteed ? `${tour.guaranteed_indicator}o` : "";
      return { id: tour.id, code: tour.code, date: tour.departure_date, price: tour.price, type, location: tour.departure_location, indicator, promoTag: dateRibbonTag(tour) };
    });
  }, [itinerary.tours, itinerary.cabinNavigation, itinerary.priceCurrency]);

  const promoPrice = localSelectedTour?.prices?.find((p: any) => p.type?.startsWith("promo"));
  const normalPrice = localSelectedTour?.prices?.find((p: any) => p.type === "normal");
  const basePrice = localSelectedTour?.price ? String(localSelectedTour.price) : null;

  const handleSelectTour = ({ date, code, id }: { date: string; code: string; id?: number }) => {
    const found = id != null
      ? itinerary.tours?.find((t: any) => t.id === id)
      : itinerary.tours?.find((t: any) => t.departure_date === date && t.code === code);
    if (found) setLocalSelectedTour(found as Tour);
  };

  // Arrow that tracks selected date card over the flights section
  const [flightArrowLeft, setFlightArrowLeft] = useState<number>(65);
  useEffect(() => {
    const form = document.querySelector<HTMLElement>('.v3.booking-form');
    if (!form) return;
    const selectedBox = form.querySelector<HTMLElement>('.tour-date-box.selected');
    const flightsEl   = form.querySelector<HTMLElement>('.flights');
    if (!selectedBox || !flightsEl) return;
    const boxRect     = selectedBox.getBoundingClientRect();
    const flightsRect = flightsEl.getBoundingClientRect();
    const center      = boxRect.left + boxRect.width / 2 - flightsRect.left;
    setFlightArrowLeft(Math.max(9, Math.round(center - 9)));
  }, [localSelectedTour]);

  const hasCabinNavigation = !!itinerary.cabinNavigation;

  const pricingCategories = useMemo(
    () => pricingCategory(localSelectedTour.room_types || "series"),
    [localSelectedTour.room_types]
  );
  // Tier rows (1st-2nd/3rd-4th/... Pax Promo) parsed from the tour's price list.
  const pricingTiers = useMemo(
    () => parsePricingTiers(localSelectedTour.prices || []),
    [localSelectedTour.prices]
  );
  const paxMultiplier = isCruise
    ? (cruiseMultiplier || 1)
    : (localSelectedTour.booking_pax_multiplier || 1);
  const maxBookingSeats = localSelectedTour.max_booking_seats || 20;
  const maxCruiseCabins = maxCabinsFor(maxBookingSeats, cruiseMultiplier);
  const cruisePaxTotal = totalCruisePax(cruiseCabins);

  const departureDate = hasCabinNavigation
    ? (cruiseDeparture?.departure_date || "")
    : localSelectedTour.departure_date;
  const cabinMeta = useMemo(() => cabinMetaFromGroups(cruiseCabinGroups), [cruiseCabinGroups]);

  // Default to one booking group of adults so counts start as a valid multiple of booking_pax_multiplier.
  const initialCounts = useMemo(() =>
    Object.keys(pricingCategories).reduce((acc, key) => ({ ...acc, [key]: key === "adult" ? paxMultiplier : 0 }), {} as Record<string, number>),
  [pricingCategories, paxMultiplier]);

  const [travelerCounts, setTravelerCounts] = useState<Record<string, number>>(initialCounts);
  const [selectedTravelers, setSelectedTravelers] = useState<string[]>(() => Array(paxMultiplier).fill("adult"));
  const [passengers, setPassengers] = useState<LocalPassenger[]>(() => Array.from({ length: paxMultiplier }, () => makePassenger("adult")));
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [passengerForm] = Form.useForm();

  const [infantDrawerOpen, setInfantDrawerOpen] = useState(false);
  const [infantParentIdx, setInfantParentIdx] = useState<number | null>(null);
  const [editingInfantIdx, setEditingInfantIdx] = useState<number | null>(null);
  const [infantForm] = Form.useForm();

  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => { new ListApi().getCountries().then(setCountries); }, []);
  const nationalityCountries = useMemo(() => withMalaysiaFirst(countries), [countries]);

  const [addressForm] = Form.useForm();
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [addressData, setAddressData] = useState({ address1: "", address2: "", state: "", city: "", postcode: "", country: "" });
  const hasAddress = !!(addressData.address1 || addressData.city);

  const [allCities, setAllCities] = useState<{ value: string; label: string; state?: string; country?: string }[]>([]);
  const [filteredCities, setFilteredCities] = useState<{ value: string; label: string }[]>([]);
  const [filteredStates, setFilteredStates] = useState<{ value: string; label: string }[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    const listApi = new ListApi();
    listApi.getCities({ country: "Malaysia" }).then((cities: any[]) => {
      const states = listApi.getStatesOptionsByCities(cities).sort((a, b) => a.label.localeCompare(b.label));
      setAllCities(cities);
      setFilteredCities(cities);
      setFilteredStates(states);
      setFilteredCountries([{ value: "Malaysia", label: "Malaysia" }]);
    });
  }, []);

  const filterCitiesByState = (selectedState: string) => {
    const cities = allCities.filter((c: any) => c.state === selectedState);
    setFilteredCities(cities.length ? cities : allCities);
  };

  const filterStatesByCity = (_: string, option: any) => {
    if (option?.state) {
      setFilteredStates([{ value: option.state, label: option.state }]);
      setFilteredCountries([{ value: option.country, label: option.country }]);
      addressForm.setFieldsValue({ state: option.state, country: option.country });
    }
  };

  const changeCityByCountry = (country: string) => {
    const listApi = new ListApi();
    listApi.getCities({ country }).then((cities: any[]) => {
      const states = listApi.getStatesOptionsByCities(cities).sort((a, b) => a.label.localeCompare(b.label));
      setAllCities(cities);
      setFilteredCities(cities);
      setFilteredStates(states);
      addressForm.setFieldsValue({ state: undefined, city: undefined });
    });
  };

  const saveAddress = async () => {
    try {
      await addressForm.validateFields();
      const vals = addressForm.getFieldsValue();
      setAddressData(vals);
      setAddressDrawerOpen(false);
    } catch { /* validation errors shown inline */ }
  };

  const [sameAsPax1, setSameAsPax1] = useState(false);
  const [showVisaDrawer, setShowVisaDrawer] = useState(false);

  const bookingInputParams: BookingInput = useMemo(() => ({
    adult: travelerCounts["adult"] || 0,
    single_supplement: travelerCounts["single_supplement"] || 0,
    child_twin: travelerCounts["child_twin"] || 0,
    child_with_bed: travelerCounts["child_with_bed"] || 0,
    child_no_bed: travelerCounts["child_no_bed"] || 0,
    allowTripleRoom: localSelectedTour.allow_triple_room || false,
  }), [travelerCounts, localSelectedTour.allow_triple_room]);

  const validation = useMemo(
    () => (hasCabinNavigation ? { isValid: true, message: "" } : validateBooking(bookingInputParams, false)),
    [bookingInputParams, hasCabinNavigation]
  );
  const isPassengerComplete = (p: any) =>
    !!p.surname && !!p.given_name && !!p.date_of_birth && !!p.nationality && !!p.phone && !!p.email;

  useEffect(() => {
    setPassengers(prev => {
      const needed = selectedTravelers.length;
      if (needed > prev.length) {
        return [...prev, ...selectedTravelers.slice(prev.length).map(cat => makePassenger(cat))];
      }
      return prev.slice(0, needed);
    });
  }, [selectedTravelers.length]);

  useEffect(() => {
    if (!hasCabinNavigation) return;
    const travelers = flattenCabinsToTravelers(cruiseCabins);
    setSelectedTravelers(travelers);
    setTravelerCounts(travelerCountsFrom(travelers));
  }, [hasCabinNavigation, cruiseCabins]);

  // Re-seed travelers on tour change so counts stay a valid multiple of paxMultiplier.
  useEffect(() => {
    if (hasCabinNavigation) return;
    setTravelerCounts(initialCounts);
    setSelectedTravelers(Array(paxMultiplier).fill("adult"));
    setPassengers(Array.from({ length: paxMultiplier }, () => makePassenger("adult")));
  }, [localSelectedTour.code]);

  useEffect(() => {
    if (!hasCabinNavigation || !cruiseTourId) return;

    let cancelled = false;
    setCruiseTourDetailLoading(true);
    new SeriesApi().getCabinNavigation(itinerary.id, "b2c2b", cruiseTourId)
      .then((cabinNavigation) => {
        if (cancelled) return;
        const detail = findCabinDetail(cabinNavigation, cruiseTourId);
        if (!detail) throw new Error("Cabin detail not found in response");
        setLocalSelectedTour({ ...detail } as Tour);
      })
      .catch((error) => {
        console.error(error);
        notification.error({ message: "An error occurred while loading the selected cabin's price." });
      })
      .finally(() => { if (!cancelled) setCruiseTourDetailLoading(false); });

    return () => { cancelled = true; };
  }, [hasCabinNavigation, cruiseTourId, itinerary.id]);

  const handleAddTraveler = (key: string) => {
    if (selectedTravelers.length + paxMultiplier > maxBookingSeats) return;
    setTravelerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + paxMultiplier }));
    setSelectedTravelers(prev => [...prev, ...Array(paxMultiplier).fill(key)]);
  };

  const handleRemoveTraveler = (key: string) => {
    // Only remove a full booking group, and never let a count go negative.
    if ((travelerCounts[key] || 0) < paxMultiplier) return;
    setTravelerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - paxMultiplier) }));
    setSelectedTravelers(prev => {
      const updated = [...prev];
      let removed = 0;
      for (let i = updated.length - 1; i >= 0 && removed < paxMultiplier; i--) {
        if (updated[i] === key) { updated.splice(i, 1); removed++; }
      }
      return updated;
    });
  };

  const stickyTabsOffset = (container: HTMLElement) =>
    (container.querySelector(".booking-form_header")?.getBoundingClientRect().height ?? 0)
    + (tabsWrapRef.current?.getBoundingClientRect().height ?? 0);

  const scrollToPassenger = (idx: number) => {
    setActivePassengerIdx(idx);
    setTabsExpanded(false); // collapse first so the card lands at the true top
    tabScrollSettleAtRef.current = Date.now() + 700;
    requestAnimationFrame(() => {
      const container = bookingScroller();
      const card = document.getElementById(`passenger-card-${idx}`);
      if (!container || !card) return;
      const prev = card.previousElementSibling;
      const target = prev?.classList.contains("cruise-cabin-badge") ? prev : card;
      const top = target.getBoundingClientRect().top
        - container.getBoundingClientRect().top
        + container.scrollTop
        - stickyTabsOffset(container)
        - 8;
      container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    });
  };

  useEffect(() => {
    if (currentStep !== 2 || passengers.length === 0) return;
    const container = bookingScroller();
    if (!container) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (Date.now() < tabScrollSettleAtRef.current) return;
        const boundary = container.getBoundingClientRect().top + stickyTabsOffset(container) + 12;
        let current = 0;
        passengers.forEach((_, i) => {
          const el = document.getElementById(`passenger-card-${i}`);
          if (el && el.getBoundingClientRect().top <= boundary) current = i;
        });
        setActivePassengerIdx(current);
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [currentStep, passengers.length]);

  useEffect(() => {
    if (tabsExpanded) return;
    const row = tabsRowRef.current;
    const tab = tabRefs.current[activePassengerIdx];
    if (!row || !tab) return;
    const left = tab.offsetLeft - row.clientWidth / 2 + tab.offsetWidth / 2;
    row.scrollTo({ left: Math.max(left, 0), behavior: "smooth" });
  }, [activePassengerIdx, tabsExpanded]);

  useLayoutEffect(() => {
    if (currentStep !== 2 || tabsExpanded) return;
    const row = tabsRowRef.current;
    if (!row) return;
    const measure = () => setTabsOverflow(row.scrollWidth > row.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [currentStep, tabsExpanded, passengers.length]);

  useEffect(() => {
    setTabsExpanded(false);
    tabRefs.current.length = passengers.length;
    setActivePassengerIdx(prev => Math.min(prev, Math.max(passengers.length - 1, 0)));
  }, [currentStep, passengers.length]);

  useEffect(() => {
    if (currentStep !== 2 || tabsExpanded || !tabsOverflow) {
      setCanScrollTabsLeft(false);
      setCanScrollTabsRight(false);
      return;
    }
    const row = tabsRowRef.current;
    if (!row) return;
    const update = () => {
      setCanScrollTabsLeft(row.scrollLeft > 2);
      setCanScrollTabsRight(row.scrollLeft < row.scrollWidth - row.clientWidth - 2);
    };
    update();
    row.addEventListener("scroll", update, { passive: true });
    return () => row.removeEventListener("scroll", update);
  }, [currentStep, tabsExpanded, tabsOverflow, passengers.length]);

  const openEditPassenger = (idx: number) => {
    const p = passengers[idx];
    setSameAsPax1(!!p.same_as_pax1);
    setEditingIdx(idx);
    passengerForm.setFieldsValue({
      given_name: p.given_name,
      surname: p.surname,
      phone: p.phone,
      email: p.email,
      designation: p.designation,
      nationality: p.nationality,
      date_of_birth: p.date_of_birth ? dayjs(p.date_of_birth, "DD/MM/YYYY") : undefined,
      nric: p.nric,
      passport_number: p.passport_number,
      passport_expiry: p.passport_expiry ? dayjs(p.passport_expiry, "DD/MM/YYYY") : undefined,
      customer_remark: p.customer_remark,
    });
  };

  const savePassenger = async () => {
    try {
      await passengerForm.validateFields();
    } catch {
      return;
    }
    const vals = passengerForm.getFieldsValue();
    if (sameAsPax1 && editingIdx !== null && editingIdx > 0) {
      vals.phone = passengers[0].phone;
      vals.email = passengers[0].email;
    }
    const dob = vals.date_of_birth ? dayjs(vals.date_of_birth).format("DD/MM/YYYY") : "";
    const expiry = vals.passport_expiry ? dayjs(vals.passport_expiry).format("DD/MM/YYYY") : "";
    setPassengers(prev => {
      const u = [...prev];
      if (editingIdx !== null) {
        u[editingIdx] = {
          ...u[editingIdx], ...vals, date_of_birth: dob, passport_expiry: expiry,
          same_as_pax1: editingIdx > 0 ? sameAsPax1 : u[editingIdx].same_as_pax1,
        };
        if (editingIdx === 0) {
          for (let i = 1; i < u.length; i++) {
            if (u[i].same_as_pax1) u[i] = { ...u[i], phone: u[0].phone, email: u[0].email };
          }
        }
      }
      return u;
    });
    setSameAsPax1(false);
    setEditingIdx(null);
    passengerForm.resetFields();
  };

  const openAddInfant = (parentIdx: number) => {
    setInfantParentIdx(parentIdx);
    setEditingInfantIdx(null);
    infantForm.resetFields();
    setInfantDrawerOpen(true);
  };

  const openEditInfant = (parentIdx: number, i: number) => {
    const inf = passengers[parentIdx].infants[i];
    setInfantParentIdx(parentIdx);
    setEditingInfantIdx(i);
    infantForm.setFieldsValue({
      given_name: inf.given_name,
      surname: inf.surname,
      gender: inf.gender,
      date_of_birth: inf.date_of_birth ? dayjs(inf.date_of_birth, "DD/MM/YYYY") : undefined,
    });
    setInfantDrawerOpen(true);
  };

  const saveInfant = async () => {
    try {
      await infantForm.validateFields();
      const vals = infantForm.getFieldsValue();
      const dob = vals.date_of_birth ? dayjs(vals.date_of_birth).format("DD/MM/YYYY") : "";
      const infant: LocalInfant = { given_name: vals.given_name || "", surname: vals.surname || "", gender: vals.gender || "", date_of_birth: dob };
      setPassengers(prev => {
        const u = [...prev];
        if (infantParentIdx !== null) {
          const infants = [...u[infantParentIdx].infants];
          if (editingInfantIdx !== null) infants[editingInfantIdx] = infant;
          else infants.push(infant);
          u[infantParentIdx] = { ...u[infantParentIdx], infants };
        }
        return u;
      });
      setInfantDrawerOpen(false);
      infantForm.resetFields();
    } catch {}
  };

  const deleteInfant = () => {
    if (infantParentIdx === null || editingInfantIdx === null) return;
    setPassengers(prev => {
      const u = [...prev];
      u[infantParentIdx!] = { ...u[infantParentIdx!], infants: u[infantParentIdx!].infants.filter((_, i) => i !== editingInfantIdx) };
      return u;
    });
    setInfantDrawerOpen(false);
    infantForm.resetFields();
  };

  // passengerIndex is the traveller's 1-based overall booking-order position, matching BookingSelection.tsx's tier boundaries.
  const getPriceForPassenger = (passengerIndex: number, cat: string) =>
    getPriceTierForPassenger(pricingTiers, passengerIndex, cat);

  // One pax item per traveller, priced off its own tier; the parent runs the promotion calculation off these items.
  useEffect(() => {
    if (!onPaxItemsChange) return;
    // The tour id travels with the items so the parent never calculates a
    // tour with another tour's pax during a tour-switch render window.
    onPaxItemsChange(selectedTravelers.map((cat, index) => ({
      category: cat,
      price: getPriceForPassenger(index + 1, cat),
      price_type: getPricingTierRow(pricingTiers, index + 1)?.type || "",
    })), localSelectedTour?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTravelers, localSelectedTour]);

  // Infants are gated purely by the tour's b2c setting (and not on cruises).
  // A zero/unset infant price no longer hides the option — the infant is added
  // and priced as "TBC" downstream.
  const canAddInfant = !hasCabinNavigation && !!localSelectedTour.has_infant_b2c;

  const infantDisabledDate = (date: dayjs.Dayjs) =>
    date.isAfter(dayjs()) ||
    date.isBefore(dayjs().subtract(2, "year")) ||
    (localSelectedTour.departure_date ? date.isAfter(dayjs(localSelectedTour.departure_date, "DD/MM/YYYY")) : false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [stateChipsExpanded, setStateChipsExpanded] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [infoAgent, setInfoAgent] = useState<Agent | null>(null);

  // Referral-link state (cookie-based, guest-facing only — see the sticky
  // lookup gating below for why logged-in customers never use this).
  const [referralCookieAgent, setReferralCookieAgent] = useState<any>(null);
  const [preferredAgent, setPreferredAgent] = useState<any>(null);
  const [dtaType, setDtaType] = useState<string>(DTA_TYPE.DIRECT);
  // Referral "change agent" request — captured reason for declining the
  // referred agent, mirrors v2's `changeAgent` payload fields.
  const [changeAgent, setChangeAgent] = useState<ReferralAgentChange | null>(null);
  const [showReferralChangeModal, setShowReferralChangeModal] = useState(false);
  const [showReferralReasonFreetext, setShowReferralReasonFreetext] = useState(false);
  const [changeAgentForm] = Form.useForm();
  const referralReasonValue = Form.useWatch("reason", changeAgentForm);
  const agentChangeReasonOptions = useMemo(
    () => Object.entries(itinerary.agentChangeReasons || {}).map(([value, label]) => ({ value, label })),
    [itinerary.agentChangeReasons]
  );

  const [stickyBinding, setStickyBinding] = useState<StickyReferralBinding | null>(null);
  const [stickyBoundAgent, setStickyBoundAgent] = useState<Agent | null>(null);
  const [stickyLookupLoading, setStickyLookupLoading] = useState(false);
  const [loggedInCustomerEmail, setLoggedInCustomerEmail] = useState("");
  const [stickyRequestSubmitted, setStickyRequestSubmitted] = useState(false);
  const [stickyRequestMessage, setStickyRequestMessage] = useState("");
  const [stickyRequestSubmitting, setStickyRequestSubmitting] = useState(false);
  const [showStickyChangeModal, setShowStickyChangeModal] = useState(false);
  const [showStickyReasonFreetext, setShowStickyReasonFreetext] = useState(false);
  const [stickyChangeForm] = Form.useForm();
  const stickyReasonValue = Form.useWatch("reason", stickyChangeForm);
  const [showStickyConfirmModal, setShowStickyConfirmModal] = useState(false);
  const [showSignInRequiredModal, setShowSignInRequiredModal] = useState(false);
  const [pendingStickyResume, setPendingStickyResume] = useState(false);
  const stickyLookupEmailRef = useRef("");    // dedup: last email actually looked up
  const stickyLookupRequestIdRef = useRef(0); // monotonic race-guard counter
  const stickyLookupTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const selectionHistoryRef = useRef<AgentSelectionHistory | null>(null);
  const currentSelectionRef = useRef<AgentSelectionHistory>({
    type: "blank",
    selectedAgentId: null,
    preferredAgent: null,
    dtaType: DTA_TYPE.DIRECT,
    changeAgent: null,
  });
  const isStickyMode = !!stickyBinding && !!stickyBoundAgent;
  const pendingStickyRequestMessage = "You have already submitted a request and it is currently within the cooldown period. Once the cooldown period ends, you will be released from this assignment and may book with a different agent for your next booking.";

  if (!isStickyMode) {
    currentSelectionRef.current = {
      type: preferredAgent?.id != null ? "referral_link" : selectedAgentId != null ? "self_select" : "blank",
      selectedAgentId,
      preferredAgent,
      dtaType,
      changeAgent,
    };
  }

  useEffect(() => {
    return () => {
      if (stickyLookupTimeoutRef.current) clearTimeout(stickyLookupTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) selectionHistoryRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || !isGuest) return;
    setChangeAgent(null);
    applyReferralCookieAgent();
  }, [open, isGuest]);

  const isValidLookupEmail = (email?: string) =>
    !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const normalizeAgentEmail = (email: any) => Array.isArray(email) ? (email[0] || "") : (email || "");

  const normalizeStickyBinding = (response: any): StickyReferralBinding | null => {
    const data = response?.data || response;
    const attrs = data?.attributes || data;
    if (!attrs) return null;
    return {
      id: data?.id || attrs.id,
      source: attrs.source,
      email: attrs.email,
      referralEntityId: attrs.referral_entity_id || attrs.agent_id || attrs.referral_agent_id,
      referralEntityType: attrs.referral_entity_type || attrs.agent_type,
      referralEntityEmail: attrs.referral_entity_email || attrs.agent_email,
      referralEntityName: attrs.referral_entity_name || attrs.agent_name || attrs.agency_full_name,
      agent: attrs.bound_agent || attrs.agent || attrs.referral_agent,
      agency: attrs.bound_agency || attrs.agency,
      subagent: attrs.bound_subagent || attrs.subagent,
      pendingStickyChangeRequest: attrs.pending_sticky_change_request || attrs.pending_request || null,
    };
  };

  const resolveStickyAgent = (binding: StickyReferralBinding): Agent | null => {
    const agentData: any = binding.agent || {};
    const agencyData: any = binding.agency || {};
    const agentId = agentData.id || binding.referralEntityId;
    const numericAgentId = Number(agentId);

    if (agentId && Number.isFinite(numericAgentId)) {
      return {
        id: numericAgentId,
        agency_id: agentData.agency_id || agencyData.id,
        username: agentData.username || agentData.name || binding.referralEntityName || "",
        name: agentData.name || agentData.full_name || binding.referralEntityName || binding.referralEntityEmail || "",
        agency_full_name: agencyData.full_name || agencyData.name || agentData.agency_full_name || binding.referralEntityName || binding.referralEntityEmail || "",
        email: normalizeAgentEmail(agentData.email || agentData.emails || agencyData.email || agencyData.emails || binding.referralEntityEmail),
        image: agentData.image,
        phone: agentData.phone || agentData.phones || agencyData.phone || agencyData.phones,
        whatsapp: agentData.whatsapp || agentData.whatsapp_no || agencyData.whatsapp || agencyData.whatsapp_no,
        address: agentData.address || agencyData.address || "",
        city: agentData.city || agencyData.city,
        state: agentData.state || agencyData.state || "",
      } as Agent;
    }

    const entityId = binding.referralEntityId?.toString();
    const entityType = binding.referralEntityType?.toLowerCase();
    const matchedAgent = agents.find(agent =>
      entityType === "agency"
        ? agent.agency_id != null && agent.agency_id.toString() === entityId
        : agent.id?.toString() === entityId
    );
    if (matchedAgent) return matchedAgent;

    if (!agentId || !Number.isFinite(numericAgentId)) return null;
    return {
      id: numericAgentId,
      agency_id: agentData.agency_id,
      username: agentData.username || agentData.name || binding.referralEntityName || "",
      name: agentData.name || agentData.full_name || binding.referralEntityName || binding.referralEntityEmail || "",
      agency_full_name: agentData.agency_full_name || binding.referralEntityName || binding.referralEntityEmail || "",
      email: normalizeAgentEmail(agentData.email || agentData.emails || binding.referralEntityEmail),
      image: agentData.image,
      phone: agentData.phone || agentData.phones,
      whatsapp: agentData.whatsapp || agentData.whatsapp_no,
      address: agentData.address || "",
      city: agentData.city,
      state: agentData.state || "",
    } as Agent;
  };

  const clearStickyState = () => {
    setStickyBinding(null);
    setStickyBoundAgent(null);
    setStickyLookupLoading(false);
    setStickyRequestSubmitted(false);
    setStickyRequestMessage("");
  };

  const restoreAgentSelectionFallback = (stickyLookupEmail = "") => {
    const selectionHistory = selectionHistoryRef.current;
    stickyLookupEmailRef.current = stickyLookupEmail;
    clearStickyState();
    if (!selectionHistory) return;

    currentSelectionRef.current = selectionHistory;
    selectionHistoryRef.current = null;
    setPreferredAgent(selectionHistory.preferredAgent);
    setSelectedAgentId(selectionHistory.selectedAgentId);
    setDtaType(selectionHistory.dtaType);
    setChangeAgent(selectionHistory.changeAgent);
  };

  const lookupStickyBinding = async (email?: string, force = false) => {
    const normalizedEmail = email?.trim().toLowerCase() || "";
    const requestId = ++stickyLookupRequestIdRef.current;

    if (!isValidLookupEmail(normalizedEmail)) {
      restoreAgentSelectionFallback(normalizedEmail);
      return null;
    }

    if (!force && normalizedEmail === stickyLookupEmailRef.current && !stickyLookupLoading) {
      return stickyBoundAgent ? { agent: stickyBoundAgent, dtaType } : null;
    }
    stickyLookupEmailRef.current = normalizedEmail;
    setStickyLookupLoading(true);

    try {
      const response = await B2C2BApi.getCustomerReferralBinding(normalizedEmail);
      if (requestId !== stickyLookupRequestIdRef.current) return null; // stale response

      const binding = normalizeStickyBinding(response);
      const boundAgent = binding ? resolveStickyAgent(binding) : null;
      if (!binding || !boundAgent) {
        if (!isGuest && !selectionHistoryRef.current) applyReferralCookieAgent();
        restoreAgentSelectionFallback(normalizedEmail);
        return null;
      }

      const resolvedDtaType = binding.source === "self_select" ? DTA_TYPE.DIRECT : DTA_TYPE.REFERRAL;
      const pendingRequest = binding.pendingStickyChangeRequest;
      if (!selectionHistoryRef.current) {
        selectionHistoryRef.current = {...currentSelectionRef.current};
      }
      setStickyBinding(binding);
      setStickyBoundAgent(boundAgent);
      setSelectedAgentId(boundAgent.id);
      setPreferredAgent(null); // sticky supersedes cookie-referral entirely
      setDtaType(resolvedDtaType);
      setChangeAgent(null);
      setStickyRequestSubmitted(pendingRequest?.status === "pending");
      setStickyRequestMessage(pendingRequest?.message || "");
      setStickyLookupLoading(false);
      return { agent: boundAgent, dtaType: resolvedDtaType };
    } catch (_error) {
      if (requestId !== stickyLookupRequestIdRef.current) return null;
      if (!isGuest && !selectionHistoryRef.current) applyReferralCookieAgent();
      restoreAgentSelectionFallback(normalizedEmail);
      return null;
    }
  };

  const lookupLoggedInCustomerStickyBinding = async () => {
    try {
      const customer = await CustomerApi.getProfile();
      const email = customer?.email || "";
      setLoggedInCustomerEmail(email);
      return lookupStickyBinding(email);
    } catch (_error) {
      restoreAgentSelectionFallback();
      return null;
    }
  };

  const handlePassengerFormValuesChange = (changedValues: any) => {
    if (!isGuest || editingIdx !== 0 || !("email" in changedValues)) return;
    if (stickyLookupTimeoutRef.current) clearTimeout(stickyLookupTimeoutRef.current);
    stickyLookupTimeoutRef.current = setTimeout(() => {
      lookupStickyBinding(changedValues.email);
    }, 500);
  };

  useEffect(() => {
    if (!open || isGuest || currentStep !== 3) return;
    lookupLoggedInCustomerStickyBinding();
  }, [open, isGuest, currentStep]);

  useEffect(() => {
    if (!pendingStickyResume || stickyLookupLoading) return;
    setPendingStickyResume(false);
    if (isStickyMode) setShowStickyChangeModal(true);
  }, [pendingStickyResume, stickyLookupLoading, isStickyMode]);

  const showPendingStickyRequestModal = (message?: string) => {
    Modal.info({
      title: "Pending change agent request",
      content: message || pendingStickyRequestMessage,
    });
  };

  const showCustomerBindingMismatchModal = (message?: string) => {
    Modal.info({
      title: "Preferred agency updated",
      content: message || "Your email account is currently assigned to an agent. We will update your preferred agency back to the previously bonded agent.",
    });
  };

  const getReferralCookieAgent = () => {
    const stored = Cookies.get("agency");
    if (!stored) return null;

    try {
      const { masterAgent, link } = JSON.parse(stored);
      return { ...masterAgent, link };
    } catch {
      Cookies.remove("agency");
      return null;
    }
  };

  const applyReferralCookieAgent = () => {
    const cookieAgent = getReferralCookieAgent();
    if (!cookieAgent) return false;

    setReferralCookieAgent(cookieAgent);
    setPreferredAgent(cookieAgent);
    setSelectedAgentId(cookieAgent.id ?? null);
    setDtaType(DTA_TYPE.REFERRAL);
    setChangeAgent(null);
    return true;
  };

  const handleStickyChangeAgentClick = () => {
    if (stickyRequestSubmitted) {
      showPendingStickyRequestModal(stickyRequestMessage);
      return;
    }

    if (isGuest) { setShowSignInRequiredModal(true); return; }
    setShowStickyConfirmModal(true);
  };

  const submitStickyChangeRequest = async (values: { reason: string; reason_other?: string }) => {
    setStickyRequestSubmitting(true);
    try {
      const response: any = await B2C2BApi.createStickyAgentChangeRequest({
        change_reason: values.reason,
        other_reason: values.reason_other,
      });
      const message = response?.message || "Your request has been submitted. You will be released from your current agent assignment after the cooldown period unless you cancel the request.";
      stickyChangeForm.resetFields();
      setShowStickyChangeModal(false);
      setShowStickyReasonFreetext(false);
      setStickyRequestSubmitted(true);
      setStickyRequestMessage(pendingStickyRequestMessage);
      notification.success({ message });
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      const errorMessage = Array.isArray(errors) ? errors.join(" ") : (err.response?.data?.error || "Unable to submit your request. Please try again.");
      if (err.response?.data?.error_code === "pending_request_exists") {
        setStickyRequestSubmitted(true);
        setStickyRequestMessage(errorMessage || pendingStickyRequestMessage);
        setShowStickyChangeModal(false);
        showPendingStickyRequestModal(errorMessage);
        return;
      }

      notification.error({ message: errorMessage });
    } finally {
      setStickyRequestSubmitting(false);
    }
  };

  const openReferralChangeModal = () => {
    setShowReferralReasonFreetext(false);
    setShowReferralChangeModal(true);
  };

  const submitReferralChangeAgent = (values: { reason: string; reason_other?: string }) => {
    if (!preferredAgent?.id) return;
    const nextChangeAgent = {
      referral_agent_id: preferredAgent.id,
      referral_link: preferredAgent.link,
      change_reason: values.reason,
      other_reason: values.reason_other,
    };

    Modal.confirm({
      title: "Change preferred agent?",
      content: "Your booking will continue with the newly selected preferred agent after you confirm.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => {
        setChangeAgent(nextChangeAgent);
        setPreferredAgent(null);
        setSelectedAgentId(null);
        setShowReferralChangeModal(false);
        setShowReferralReasonFreetext(false);
        changeAgentForm.resetFields();
        notification.success({ message: "Request Submitted. You can choose a new agent." });
      },
    });
  };

  const stateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    agents.forEach(a => { if (a.state) counts[a.state] = (counts[a.state] || 0) + 1; });
    return counts;
  }, [agents]);

  const allStates = useMemo(() => Object.keys(stateCounts), [stateCounts]);

  const filteredAgents = useMemo(() => {
    let result = agents;
    if (selectedState) result = result.filter(a => a.state === selectedState);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a => a.name?.toLowerCase().includes(q) || a.address?.toLowerCase().includes(q) || a.city?.toLowerCase().includes(q));
    }
    return result;
  }, [agents, selectedState, searchQuery]);

  // A referral-cookie or sticky-bound agent may be a synthesized object that
  // isn't in the `agents` list, so fall back to the ones we already hold in state.
  const selectedAgent = useMemo(
    () =>
      agents.find(a => a.id === selectedAgentId) ??
      [preferredAgent, referralCookieAgent, stickyBoundAgent].find(a => a?.id === selectedAgentId) ??
      null,
    [agents, selectedAgentId, preferredAgent, referralCookieAgent, stickyBoundAgent]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fareType, setFareType] = useState<PAYMENT_TERMS>(PAYMENT_TERMS.FULL_PAYMENT);
  const [selectedChannel, setSelectedChannel] = useState<typeof PAYMENT_CHANNELS[0] | null>(null);
  const [selectedBank, setSelectedBank] = useState<string>("");
  const [bankDrawerOpen, setBankDrawerOpen] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  // One {category, price, price_type} entry per traveller, priced off its tier; shared by baseFare, priceBreakdown, and onPaxItemsChange.
  const paxPriceItems = useMemo(
    () => selectedTravelers.map((cat, index) => ({
      category: cat,
      price: getPriceForPassenger(index + 1, cat),
      price_type: getPricingTierRow(pricingTiers, index + 1)?.type || "",
    })),
    [selectedTravelers, pricingTiers]
  );

  // Base fare = sum of each traveller's own tiered price (before add-ons / fees).
  const baseFare = useMemo(
    () => paxPriceItems.reduce((sum, item) => sum + item.price, 0),
    [paxPriceItems]
  );

  const totalPax = selectedTravelers.length;
  const depositPerPax = localSelectedTour.b2c2b_customer_deposit || 0;
  const depositTotal = depositPerPax * totalPax;
  const canPayDeposit = !!localSelectedTour.allow_customer_pay_deposit;

  // Full V2-parity fees: add-ons + non-Malaysian charge + admin/CC fee.
  // Add-ons: addon_others + compulsory_additional_fee per pax (utils/helpers).
  const bookingAddons = useMemo(() => addons(localSelectedTour, totalPax), [localSelectedTour, totalPax]);
  const addOnsTotal = bookingAddons.reduce((sum, a) => sum + a.value, 0);

  // Non-Malaysian charge: per-pax surcharge for each non-Malaysian passenger.
  const nonMalaysiaPassengerCount = useMemo(
    () => passengers.filter(p => {
      const nat = (p?.nationality || "").toString().trim().toLowerCase();
      return !!nat && nat !== "malaysia";
    }).length,
    [passengers]
  );
  const nonMalaysiaChargeAmount = Number(localSelectedTour.non_malaysian_charge || 0) * nonMalaysiaPassengerCount;

  // Visa-fee deduction: the backend selling_price subtracts a per-pax visa_fee,
  // so mirror it here to keep the displayed total equal to what is charged.
  const visaDeduction = Number(localSelectedTour.visa_fee || 0) * totalPax;

  // Admin/CC fee: card surcharge applied only on the payment step. Gate on currentStep
  // (not just the channel) so it doesn't linger in the total after navigating back.
  const ccRate = itinerary.ccRate;
  const showAdminFee = currentStep === TOTAL_STEPS
    && selectedChannel?.value === "public_bank_2"
    && ccRate !== undefined && ccRate > 0;
  // Full-payment fee is charged on the whole payable; deposit fee only on the deposit (mirrors V2).
  // Promotion deduction comes from the backend calculator (never computed
  // locally); subtracted before the admin fee since the card fee is charged
  // on the post-promotion payable.
  const preAdminTotal = Math.max(0, baseFare + addOnsTotal + nonMalaysiaChargeAmount - visaDeduction - promotionTotal);
  const adminFee = showAdminFee ? preAdminTotal * (ccRate as number) : 0;
  const depositAdminFee = showAdminFee ? depositTotal * (ccRate as number) : 0;

  const grandTotal = preAdminTotal + adminFee;
  const depositGrandTotal = depositTotal + depositAdminFee;

  const bankOptions = useMemo(() => {
    if (!selectedChannel || !paymentMethods) return [];
    const banks = paymentMethods[selectedChannel.bankName] || [];
    if (!Array.isArray(banks) || banks.length === 0) return [];
    // Offline banks (dobw only) come back with an empty redirect_url (no endpoint) and are disabled in the UI.
    const isDobw = selectedChannel.value === "dobw";
    if (Array.isArray(banks[0])) {
      return (banks as [string, string, string?][]).map(([label, value, redirect_url]) => ({
        label, value, redirect_url: redirect_url || "",
        disabled: isDobw && !redirect_url,
      }));
    }
    return banks.map((b: any) =>
      typeof b === "string" ? { label: b, value: b, redirect_url: "", disabled: false } : { redirect_url: "", disabled: false, ...b }
    );
  }, [selectedChannel, paymentMethods]);

  const needsBankSelection = !!(selectedChannel && bankOptions.length > 0);
  const canProceedStep4 = !!selectedChannel && (!needsBankSelection || !!selectedBank) && termsAgreed && privacyAgreed;

  const travellerSummary = Object.entries(travelerCounts)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => `${count} ${String(pricingCategories[key])}`)
    .join(", ");

  const displayAmount = fareType === PAYMENT_TERMS.DEPOSIT
    ? currencyDisplay(itinerary.priceCurrency, depositGrandTotal)
    : currencyDisplay(itinerary.priceCurrency, grandTotal);

  // Per-category price breakdown for the Price Summary popup; sums real per-traveller prices, not unit price x count.
  const priceBreakdown = Object.entries(travelerCounts)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => {
      const items = paxPriceItems.filter((item) => item.category === key);
      return {
        key,
        label: String(pricingCategories[key] ?? key),
        count,
        sum: items.reduce((sum, item) => sum + item.price, 0),
        tiers: groupFareTiers(items),
      };
    });

  // Admin fee is levied on whatever is charged now (full total, or the deposit).
  const adminFeeShown = fareType === PAYMENT_TERMS.DEPOSIT ? depositAdminFee : adminFee;
  // Balance still owed after the deposit (tour cost only; V2 charges any card fee at balance time).
  const remainingBalance = preAdminTotal - depositTotal;
  const hasPriceBreakdown = priceBreakdown.length > 0;

  const currentStepData = STEPS.find(s => s.step === currentStep)!;
  const isLastStep = currentStep === TOTAL_STEPS;

  const handleBack = () => {
    if (currentStep === 1) { onClose(); return; }
    setCurrentStep(prev => prev - 1);
  };

  const handleNext = () => {
    if (isLastStep) return;
    if (currentStep === 1) {
      if (hasCabinNavigation) {
        if (!cruiseTourId) {
          notification.warning({ message: "Please choose a room type and cabin" });
          return;
        }
        if (!travellersConfirmed) {
          setTravellersDrawerOpen(true);
          return;
        }
      }
    }
    if (currentStep === 2) {
      if (!validation.isValid) return;
      const incompleteIdxs = passengers.map((p, idx) => isPassengerComplete(p) ? -1 : idx).filter(i => i !== -1);
      if (incompleteIdxs.length > 0) {
        setIncompletePassengerIdxs(incompleteIdxs);
        setTimeout(() => setIncompletePassengerIdxs([]), 2000);
        notification.warning({ message: "Please complete all required passengers details" });
        scrollToPassenger(incompleteIdxs[0]);
        return;
      }
    }
    if (currentStep === 3 && (!selectedAgentId || stickyLookupLoading)) return;
    setCurrentStep(prev => prev + 1);
    setTimeout(() => { document.querySelector(".booking-drawer .ant-drawer-body")?.scrollTo({ top: 0 }); }, 0);
  };

  const handlePayment = async () => {
    // Pre-submission guards — only Pax 1 has required fields per design
    const pax1 = passengers[0];
    if (!pax1?.surname || !pax1?.given_name || !pax1?.phone || !pax1?.email) {
      notification.warning({ message: "Please complete all required details for Passenger 1 before proceeding." });
      return;
    }
    if (!selectedAgentId) {
      notification.warning({ message: "Please select an agent." });
      return;
    }
    if (!selectedChannel) {
      notification.warning({ message: "Please select a payment method." });
      return;
    }

    if (stickyLookupTimeoutRef.current) clearTimeout(stickyLookupTimeoutRef.current);
    const staleSelectedAgentId = selectedAgentId;
    let submissionAgentId = selectedAgentId;
    let submissionAgent = selectedAgent;
    let submissionDtaType = dtaType;
    let submissionPreferredAgent = preferredAgent;
    let submissionChangeAgent = changeAgent;
    const useCurrentSelection = () => {
      const selection = currentSelectionRef.current;
      submissionAgentId = selection.selectedAgentId;
      submissionAgent = agents.find(agent => agent.id === selection.selectedAgentId)
        ?? (selection.preferredAgent?.id === selection.selectedAgentId ? selection.preferredAgent : null);
      submissionDtaType = selection.dtaType;
      submissionPreferredAgent = selection.preferredAgent;
      submissionChangeAgent = selection.changeAgent;
    };
    if (!isGuest) {
      const reconfirmed = await (loggedInCustomerEmail ? lookupStickyBinding(loggedInCustomerEmail, true) : lookupLoggedInCustomerStickyBinding());
      if (reconfirmed) {
        if (staleSelectedAgentId != null && reconfirmed.agent.id !== staleSelectedAgentId) {
          showCustomerBindingMismatchModal();
          return;
        }

        submissionAgentId = reconfirmed.agent.id;
        submissionAgent = reconfirmed.agent;
        submissionDtaType = reconfirmed.dtaType;
        submissionPreferredAgent = null;
        submissionChangeAgent = null;
      } else useCurrentSelection();
    } else {
      const reconfirmed = await lookupStickyBinding(pax1?.email, true);
      if (reconfirmed) {
        if (staleSelectedAgentId != null && reconfirmed.agent.id !== staleSelectedAgentId) {
          showCustomerBindingMismatchModal();
          return;
        }

        submissionAgentId = reconfirmed.agent.id;
        submissionAgent = reconfirmed.agent;
        submissionDtaType = reconfirmed.dtaType;
        submissionPreferredAgent = null;
        submissionChangeAgent = null;
      } else useCurrentSelection();
    }

    if (!submissionAgentId) {
      notification.warning({ message: "Please select an agent." });
      return;
    }

    setIsSubmitting(true);
    try {
      const priceType = localSelectedTour.prices?.find((p: any) => p.type === "normal")?.type
        ?? localSelectedTour.prices?.[0]?.type
        ?? "normal";

      // Build price_types in V2 format: one entry per category per pricing tier row.
      // V2 expands each pricing row into { name, price, type, dta } per category.
      const categoryNames = [...Object.keys(pricingCategories), "infant"];
      const price_types: any[] = [];
      (localSelectedTour.prices ?? [])
        .filter((p: any) => p.type === priceType)
        .forEach((pRow: any) => {
          categoryNames.forEach(catName => {
            const price = pRow[catName];
            const dta = pRow[`dta_${catName}`] || 0;
            if (price !== undefined && price !== null) {
              price_types.push({ name: catName, price, type: pRow.type, dta });
            }
          });
        });

      // booking_seats in V2 format: "N normal(adultPrice)"
      const priceRow = (localSelectedTour.prices ?? []).find((p: any) => p.type === priceType);
      const adultPrice = Number(priceRow?.adult || 0);
      const bookingSeats = `${totalPax} ${priceType}(${adultPrice})`;

      // DuitNow banks store redirect_url as the 3rd element; non-DuitNow banks don't need it.
      const selectedBankOption = bankOptions.find(b => b.value === selectedBank);
      const redirect_url = selectedBankOption?.redirect_url ?? "";

      // V2 splits "BANKCODE - TYPE" values for DuitNow into separate method / bank_type fields.
      let payment_method = selectedBank || (selectedChannel?.value ?? "");
      let bank_type = "";
      if (selectedChannel?.value === "dobw" && payment_method.includes(" - ")) {
        const parts = payment_method.split(" - ");
        payment_method = parts[0] || payment_method;
        bank_type = parts[1] || "";
      }

      const payload = {
        tour_id: hasCabinNavigation ? (cruiseTourId ?? localSelectedTour.id) : localSelectedTour.id,
        agree: termsAgreed && privacyAgreed,
        bookings: passengers.map(p => ({
          category: p.category,
          given_name: p.given_name,
          surname: p.surname,
          mobile: p.phone,
          phone: p.phone,
          email: p.email,
          designation: p.designation,
          nationality: p.nationality,
          date_of_birth: p.date_of_birth,
          nric: p.nric,
          passport_number: p.passport_number,
          passport_expiry_date: p.passport_expiry,
          customer_remark: p.customer_remark,
          infants: p.infants,
          price_types,
        })),
        booking_seats: bookingSeats,
        sub_sale_rep: submissionAgent?.username || submissionAgent?.name || submissionAgent?.agency_full_name || "",
        agent_id: Number(submissionAgentId),
        address_1: addressData.address1 || "",
        address_2: addressData.address2 || "",
        city_name: addressData.city || "",
        state: addressData.state || "",
        country: addressData.country || "",
        postal_code: addressData.postcode || "",
        payment: {
          gateway: selectedChannel?.value ?? "",
          method: payment_method,
          bank_type,
          redirect_url,
        },
        set_amount: 0,
        suka_insurance: null,
        sg_insurance: null,
        fare_type_selected: fareType,
        ...(fareType === PAYMENT_TERMS.DEPOSIT ? { fare_type: "deposit" } : {}),
        register_as_member: isGuest,
        business_type: "b2c2b",
        dta_type: submissionDtaType,
        ...(submissionDtaType === DTA_TYPE.REFERRAL && submissionPreferredAgent?.id ? {
          referral_agent_id: submissionPreferredAgent.id,
          new_agent_id: Number(submissionAgentId),
          referral_link: submissionPreferredAgent.link || "",
        } : {}),
        ...(submissionChangeAgent ? { ...submissionChangeAgent, new_agent_id: Number(submissionAgentId) } : {}),
      };

      const response = await B2C2BApi.saveBooking(payload);
      if (response.error) {
        if (response.error_code === "customer_referral_binding_mismatch") {
          showCustomerBindingMismatchModal(response.error);
        } else {
          notification.error({ message: response.error });
        }
        setIsSubmitting(false);
      } else {
        sessionStorage.setItem('ice_v3_payment', '1');
        location.href = response.redirect_to;
        // overlay stays visible while browser navigates away
      }
    } catch (err: any) {
      console.error('[handlePayment]', err?.response?.data ?? err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      if (err?.response?.data?.error_code === "customer_referral_binding_mismatch") {
        showCustomerBindingMismatchModal(msg);
      } else {
        notification.error({ message: msg });
      }
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <>
      <TourDateSelectorV3
        dates={dates}
        selectedDate={hasCabinNavigation ? (cruiseDeparture?.departure_date || "") : localSelectedTour.departure_date}
        selectedTourCode={hasCabinNavigation ? cruiseDeparture?.departure_date : localSelectedTour.code}
        selectTour={selectTour ?? handleSelectTour}
        category={hasCabinNavigation ? CRUISE_CATEGORY : (itinerary.category || "")}
        isNew={!!itinerary.isNew}
        isLoading={isLoading}
        open={open}
      />
      <FlightScheduleV3
        flights={localSelectedTour.flights}
        guaranteedDeparture={localSelectedTour.guaranteed_departure}
        arrowLeft={flightArrowLeft}
      />
      <PromoBannerV3
        priceCurrency={itinerary.priceCurrency}
        promoPrice={promoPrice}
        normalPrice={normalPrice}
        basePrice={basePrice}
        onClick={() => setShowPriceChart(true)}
      />
      {isCruise && (
        <CruiseCabinSelectorV3
          departure={cruiseDeparture}
          priceCurrency={itinerary.priceCurrency || "MYR"}
          selectedMultiplier={cruiseMultiplier}
          selectedTourId={cruiseTourId}
          onSelectRoomType={(roomType) => onSelectCruiseRoomType?.(roomType)}
          onSelectCabin={(cabin) => onSelectCruiseCabin?.(cabin)}
          cabinSelectionDisabled={cruiseTourDetailLoading}
        />
      )}
      <Drawer
        title={<span className="section-label">Price Chart</span>}
        placement="bottom"
        height="70%"
        open={showPriceChart}
        onClose={() => setShowPriceChart(false)}
        className="inclusions-drawer"
      >
        <div style={{ padding: "16px 20px" }}>
          <PromotionTagsV3
            primary={localSelectedTour.promo_tag || undefined}
            others={(localSelectedTour.other_tags || []).map((tag) => tag.name)}
          />
          <PriceChartV3
            priceCurrency={itinerary.priceCurrency || "MYR"}
            prices={localSelectedTour.prices || []}
            roomTypes={localSelectedTour.room_types}
          />
        </div>
      </Drawer>
    </>
  );

  const renderStep2 = () => {
    const editingPassenger = editingIdx !== null ? passengers[editingIdx] : null;

    return (
      <>
        <div className="policy-notice">
          <div className="policy-notice_icon"><img src="/iceholidays-assets/images/v3/policy-notice-icon.svg" alt="" aria-hidden="true" width={32} height={32} /></div>
          <div className="policy-notice_text">
            <span className="policy-notice_title">Child Age Policy</span>
            <span className="policy-notice_body">
              Traveller age is based on the departure date. If the child turns older by departure day, the higher age category applies.
            </span>
          </div>
        </div>

        {hasCabinNavigation ? (
          <button
            type="button"
            className="cruise-travellers-summary"
            onClick={() => setTravellersDrawerOpen(true)}
          >
            <img
              className="cruise-travellers-summary_icon"
              src="/iceholidays-assets/images/v3/icon-cruise-travellers.svg"
              alt=""
              aria-hidden="true"
              width={40}
              height={40}
            />
            <span className="cruise-travellers-summary_text">
              <span className="cruise-travellers-summary_label">Travellers</span>
              <span className="cruise-travellers-summary_value">{cruisePaxSummary(cruiseCabins)}</span>
            </span>
            <span className="cruise-travellers-summary_chevron">
              <Icon path={mdiChevronDown} size="24px" />
            </span>
          </button>
        ) : (
        <div className="pax-list">
          {Object.entries(pricingCategories).map(([key, label]) => {
            const age = childAgeLabel(key, itinerary.child_age_ranges);
            return (
            <div key={key} className="pax-row">
              <div className="pax-row_info">
                <span className="pax-row_label">{String(label)}</span>
                {age && <span className="pax-row_age">{age}</span>}
              </div>
              <div className="pax-row_controls">
                <Button shape="circle" className="pax-btn pax-btn--minus" icon={<Icon path={mdiMinus} size="18px" />} onClick={() => handleRemoveTraveler(key)} disabled={(travelerCounts[key] || 0) < paxMultiplier} />
                <span className="pax-count">{travelerCounts[key] || 0}</span>
                <Button shape="circle" className="pax-btn pax-btn--plus" icon={<Icon path={mdiPlus} size="18px" />} onClick={() => handleAddTraveler(key)} disabled={selectedTravelers.length + paxMultiplier > maxBookingSeats} />
              </div>
            </div>
            );
          })}
          {selectedTravelers.length > 0 && !validation.isValid && (
            <div className="validation-error">
              <img src="/iceholidays-assets/images/v3/policy-error-icon.svg" alt="" aria-hidden="true" width={24} height={24} />
              <span>{validation.message}</span>
            </div>
          )}
        </div>
        )}

        {passengers.length > 0 && (
          <div className="passengers">
            {!hasCabinNavigation && <span className="passengers_title">Travellers Details</span>}
            <div className={`passenger-tabs${tabsExpanded ? " passenger-tabs--expanded" : ""}`} ref={tabsWrapRef}>
              <div
                className={`passenger-tabs_row${canScrollTabsLeft ? " passenger-tabs_row--fade-left" : ""}${canScrollTabsRight ? " passenger-tabs_row--fade-right" : ""}`}
                ref={tabsRowRef}
              >
                {passengers.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    ref={el => { tabRefs.current[idx] = el; }}
                    className={`passenger-tab${idx === activePassengerIdx ? " passenger-tab--active" : ""}${incompletePassengerIdxs.includes(idx) ? " passenger-tab--incomplete" : ""}`}
                    onClick={() => scrollToPassenger(idx)}
                  >
                    {`Passenger ${idx + 1}`}
                  </button>
                ))}
              </div>
              {tabsOverflow && (
                <button
                  type="button"
                  className="passenger-tabs_toggle"
                  aria-expanded={tabsExpanded}
                  aria-label={tabsExpanded ? "Collapse passenger tabs" : "Expand passenger tabs"}
                  onClick={() => setTabsExpanded(v => !v)}
                >
                  <Icon path={mdiChevronDown} size="20px" />
                </button>
              )}
            </div>
            {passengers.map((p, idx) => {
              const phoneDisplay = p.phone ?? "";
              const meta = cabinMeta?.[idx];
              return (
                <React.Fragment key={idx}>
                {meta?.isStart && (
                  <div className="cruise-cabin-badge">
                    <img src="/iceholidays-assets/images/v3/icon-cruise-cabin.svg" alt="" aria-hidden="true" width={24} height={24} />
                    <span>Cabin {meta.cabinNo}</span>
                  </div>
                )}
                <div id={`passenger-card-${idx}`} className={`passenger-card${incompletePassengerIdxs.includes(idx) ? " passenger-card--highlight" : ""}`} onClick={() => openEditPassenger(idx)} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && openEditPassenger(idx)}>
                  <div className="passenger-card_header">
                    <span className="passenger-card_name">Passenger {idx + 1}</span>
                    <span className="passenger-card_edit">Edit</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Family name/Surname on Passport</span>
                    <span className={`passenger-card_value${!p.surname ? " placeholder" : ""}`}>{p.surname || "Please Fill Up"}</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Given name on Passport</span>
                    <span className={`passenger-card_value${!p.given_name ? " placeholder" : ""}`}>{p.given_name || "Please Fill Up"}</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Date of Birth</span>
                    <span className={`passenger-card_value${!p.date_of_birth ? " placeholder" : ""}`}>{p.date_of_birth || "Please Fill Up"}</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Mobile Number</span>
                    <span className={`passenger-card_value${!p.phone ? " placeholder" : ""}`}>{phoneDisplay || "Please Fill Up"}</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Email</span>
                    <span className={`passenger-card_value${!p.email ? " placeholder" : ""}`}>{p.email || "Please Fill Up"}</span>
                  </div>
                  <div className="passenger-card_row">
                    <span className="passenger-card_field">Nationality</span>
                    <span className={`passenger-card_value${!p.nationality ? " placeholder" : ""}`}>{p.nationality || "Please Fill Up"}</span>
                  </div>
                  {p.infants.length > 0 && (
                    <>
                      <div className="passenger-card_divider" />
                      <div className="passenger-card_infants">
                        {p.infants.map((inf, i) => (
                          <button
                            key={i}
                            type="button"
                            className="passenger-card_infant-tag"
                            onClick={e => { e.stopPropagation(); openEditInfant(idx, i); }}
                          >
                            {`Infant ${i + 1}`}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        <Drawer
          title={editingIdx !== null ? `Passenger ${editingIdx + 1}` : ""}
          placement="bottom"
          height="100%"
          open={editingIdx !== null}
          onClose={() => { setSameAsPax1(false); setEditingIdx(null); passengerForm.resetFields(); setInfantDrawerOpen(false); setInfantParentIdx(null); infantForm.resetFields(); if (stickyLookupTimeoutRef.current) clearTimeout(stickyLookupTimeoutRef.current); }}
          className="passenger-drawer"
          rootClassName="v3"
          footer={<Button type="primary" size="large" block onClick={savePassenger} className="passenger-drawer_done-btn">Done</Button>}
        >
          {editingPassenger && (
            <>
              <p className="passenger-drawer_subtitle">
                Fields marked <span className="passenger-drawer_req">*</span> are required. Optional fields help us serve you better.
              </p>
              {editingIdx === 0 && (
                <div className="passenger-drawer_banner">
                  <span className="passenger-drawer_banner-icon">
                    <img src="/iceholidays-assets/images/v3/passenger-banner-icon.svg" alt="" aria-hidden="true" width={20} height={20} />
                  </span>
                  <span>You'll be registered as a member for a more customised experience.</span>
                </div>
              )}

              <Form form={passengerForm} layout="vertical" onValuesChange={handlePassengerFormValuesChange}>
                {/* Section 1 — Required Info */}
                <div className="required-info-section">
                  <Collapse
                    expandIconPosition="end"
                    className="drawer-collapse"
                    defaultActiveKey={["required"]}
                    items={[{
                      key: "required",
                      label: <SectionHeader num={1} title="Required Info" />,
                      children: (
                        <>
                          <Form.Item label="Fare Category">
                            <Input
                              value={(() => {
                                const amt = editingIdx !== null ? getPriceForPassenger(editingIdx + 1, editingPassenger.category) : 0;
                                return `${String(pricingCategories[editingPassenger.category])}${amt ? ` (${currencyDisplay(itinerary.priceCurrency, amt)})` : ""}`;
                              })()}
                              disabled
                              size="large"
                            />
                          </Form.Item>

                          <Form.Item label="Family name/Surname on Passport" name="surname" rules={[{ required: true, message: "Surname is required" }]}>
                            <Input placeholder="e.g. Surname: Smith, Given Name: John" size="large" />
                          </Form.Item>
                          <Form.Item label="Given name on Passport" name="given_name" rules={[{ required: true, message: "Given name is required" }]}>
                            <Input placeholder="Given name on Passport" size="large" />
                          </Form.Item>
                          <Form.Item label="Date of Birth" name="date_of_birth" rules={[{ required: true, message: "Date of Birth is required" }]}>
                            <DobDatePicker
                              format="DD/MM/YYYY"
                              placeholder="DD/MM/YYYY"
                              size="large"
                              style={{ width: "100%" }}
                              disabledDate={d => categoryDobDisabledDate(d, editingPassenger.category, localSelectedTour.departure_date, itinerary.child_age_ranges)}
                              suffixIcon={<Icon path={mdiCalendarMonth} size="20px" />}
                            />
                          </Form.Item>

                          {editingIdx === 0 ? (
                            /* Pax 1 — Phone*, Email*, Nationality* */
                            <>
                              <Form.Item label="Mobile Number" name="phone" rules={[{ required: true, message: "Phone is required" }]}>
                                <PhoneNumberInput />
                              </Form.Item>
                              <Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Valid email is required" }]}>
                                <Input placeholder="e.g. johnsmit@gmail.com" size="large" type="email" />
                              </Form.Item>
                              <Form.Item
                                label={
                                  <div className="nationality-label-wrapper">
                                    <span className="nationality-label">
                                      Nationality
                                    </span>
                                    <button type="button" className="visa-badge" onClick={() => setShowVisaDrawer(true)}>
                                        <Icon path={mdiInformationOutline} size="14px" /> VISA Information
                                    </button>
                                  </div>
                                }
                                name="nationality"
                                rules={[{ required: true, message: "Nationality is required" }]}
                              >
                                <Select showSearch options={nationalityCountries} placeholder="Please Select" size="large" />
                              </Form.Item>
                            </>
                          ) : (
                            /* Pax 2+ — Nationality*, then toggle, then conditional phone/email */
                            <>
                              <Form.Item
                                label={
                                  <div className="nationality-label-wrapper">
                                    <span className="nationality-label">
                                      Nationality
                                    </span>
                                    <button type="button" className="visa-badge" onClick={() => setShowVisaDrawer(true)}>
                                        <Icon path={mdiInformationOutline} size="14px" /> VISA Information
                                    </button>
                                  </div>
                                }
                                name="nationality"
                                rules={[{ required: true, message: "Nationality is required" }]}
                              >
                                <Select showSearch options={nationalityCountries} placeholder="Please Select" size="large" />
                              </Form.Item>
                              <hr className="passenger-drawer_divider" />
                              <div className="same-as-pax1-row">
                                <Switch
                                  checked={sameAsPax1}
                                  onChange={checked => {
                                    setSameAsPax1(checked);
                                    if (checked) {
                                      passengerForm.setFieldsValue({ phone: passengers[0].phone, email: passengers[0].email });
                                    } else {
                                      passengerForm.setFieldsValue({ phone: "", email: "" });
                                    }
                                  }}
                                />
                                <span className="same-as-pax1-row_label">Contact details same as Passenger 1</span>
                              </div>
                              {!sameAsPax1 && (
                                <>
                                  <Form.Item label="Mobile Number" name="phone" rules={[{ required: true, message: "Phone is required" }]}>
                                    <PhoneNumberInput />
                                  </Form.Item>
                                  <Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Valid email is required" }]}>
                                    <Input placeholder="e.g. johnsmit@gmail.com" size="large" type="email" />
                                  </Form.Item>
                                </>
                              )}
                            </>
                          )}
                        </>
                      ),
                    }]}
                  />
                </div>

                {/* Section 2 — Additional Traveller Info */}
                <div className="enhance-section">
                  <div className="faster-checkin-badge">
                    <span className="faster-checkin-badge_icon"><Icon path={mdiLightningBolt} size={0.8} /></span>
                    <span>For Faster Check-In</span>
                  </div>
                  <Collapse
                    expandIconPosition="end"
                    className="drawer-collapse"
                    items={[{
                      key: "enhance",
                      label: <SectionHeader num={2} title="Additional Traveller Info" />,
                      children: (
                        <>
                          <Form.Item label="Designation" name="designation">
                            <Select options={DESIGNATIONS} placeholder="Please Select" size="large" />
                          </Form.Item>

                          <Form.Item label="NRIC" name="nric">
                            <Input placeholder="e.g. 901010010001" maxLength={12} size="large" />
                          </Form.Item>
                          <Form.Item label="Passport Number" name="passport_number">
                            <Input placeholder="e.g. A12345678" size="large" />
                          </Form.Item>
                          <Form.Item label="Passport Expiry" name="passport_expiry">
                            <DatePicker
                              format="DD/MM/YYYY"
                              placeholder="DD/MM/YYYY"
                              size="large"
                              style={{ width: "100%" }}
                              disabledDate={d => passportExpiryDisabledDates(d, localSelectedTour.departure_date)}
                            />
                          </Form.Item>
                          <Form.Item label="Passport Photocopy" name="passport_photocopy" getValueFromEvent={e => e?.file} valuePropName="file">
                            <Upload {...b2c2bUploadProps} maxCount={1} multiple={false}>
                              <Input
                                placeholder="Upload document"
                                size="large"
                                readOnly
                                suffix={<Icon path={mdiFileUploadOutline} size="19px" />}
                              />
                            </Upload>
                          </Form.Item>
                          <Form.Item label="Remark" name="customer_remark">
                            <Input.TextArea placeholder="e.g. I'm a vegan" rows={3} />
                          </Form.Item>
                        </>
                      ),
                    }]}
                  />
                </div>
              </Form>

              {/* Section 3 — Travelling with infant (not applicable to Child category) */}
              {canAddInfant && !editingPassenger.category?.toLowerCase().includes("child") && (
                <div className="infant-section">
                  <div className="drawer-section-header drawer-section-header--plain">
                    <span className="drawer-section-header_num">3</span>
                    <span className="drawer-section-header_title">Travelling with infant?</span>
                  </div>
                  {editingPassenger.infants.map((inf, i) => (
                    <div
                      key={i}
                      className="infant-card"
                      onClick={() => editingIdx !== null && openEditInfant(editingIdx, i)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => e.key === "Enter" && editingIdx !== null && openEditInfant(editingIdx, i)}
                    >
                      <div className="infant-card_header">
                        <span className="infant-card_name">Infant {i + 1}</span>
                        <span className="infant-card_edit">Edit</span>
                      </div>
                      <div className="infant-card_row">
                        <span className="infant-card_field">Date of Birth</span>
                        <span className="infant-card_value">{inf.date_of_birth || "-"}</span>
                      </div>
                    </div>
                  ))}
                  <Button block className="add-infant-btn" onClick={() => editingIdx !== null && openAddInfant(editingIdx)} icon={<img src="/iceholidays-assets/images/v3/add-infant-icon.svg" alt="" aria-hidden="true" width={24} height={24} />}>Add Infant</Button>
                </div>
              )}

            </>
          )}
        </Drawer>

        {/* Infant detail drawer — sibling to passenger drawer, portals to body */}
        <Drawer
          title="Infant Details"
          placement="bottom"
          height="100%"
          open={infantDrawerOpen}
          onClose={() => { setInfantDrawerOpen(false); setInfantParentIdx(null); infantForm.resetFields(); }}
          className="infant-drawer"
          rootClassName="v3"
          footer={
            editingInfantIdx !== null ? (
              <Flex gap={12}>
                <Button danger ghost size="large" style={{ flex: 1 }} onClick={deleteInfant}>Delete Infant</Button>
                <Button type="primary" size="large" style={{ flex: 1 }} onClick={saveInfant}>Update</Button>
              </Flex>
            ) : (
              <Button type="primary" size="large" block onClick={saveInfant}>Done</Button>
            )
          }
        >
          <p className="passenger-drawer_subtitle">
            Fields marked <span className="passenger-drawer_req">*</span> are required. Optional fields help us serve you better.
          </p>
          <Form form={infantForm} layout="vertical">
            <Form.Item label="Date of Birth" name="date_of_birth" rules={[{ required: true, message: "Date of Birth is required" }]}>
              <DobDatePicker
                format="DD/MM/YYYY"
                size="large"
                style={{ width: "100%" }}
                disabledDate={infantDisabledDate}
                placeholder="Please Select"
                suffixIcon={<Icon path={mdiCalendarMonth} size="20px" />}
              />
            </Form.Item>
            <Form.Item label="Gender" name="gender">
              <Select size="large" placeholder="Please Select" style={{ width: "100%" }} options={[{ label: "Male", value: "male" }, { label: "Female", value: "female" }]} />
            </Form.Item>
            <Form.Item label="Surname on Passport" name="surname">
              <Input placeholder="Infant surname" size="large" />
            </Form.Item>
            <Form.Item label="Given name on Passport" name="given_name">
              <Input placeholder="Infant given name" size="large" />
            </Form.Item>
          </Form>
        </Drawer>

        <Drawer
          title="Visa Information"
          placement="bottom"
          height="auto"
          open={showVisaDrawer}
          onClose={() => setShowVisaDrawer(false)}
          rootClassName="v3 visa-info-drawer"
          closable
        >
          <div className="visa-info-drawer_body">
            <p>VISA may be required. Kindly reconfirm the VISA requirements with your travel agent.</p>
            <p>If a VISA is required, please apply before making the booking. All payments are non-refundable in the event that the visa is not approved.</p>
          </div>
        </Drawer>
      </>
    );
  };

  const renderAgentCard = (agent: Agent, locked = false, onSelect?: () => void) => {
    const isSelected = selectedAgentId === agent.id;
    return (
      <div
        key={agent.id}
        className={`agent-card${isSelected ? " agent-card--selected" : ""}${locked ? " agent-card--locked" : ""}`}
        onClick={locked ? undefined : (onSelect ?? (() => setSelectedAgentId(agent.id)))}
      >
        <div className={`radio${isSelected ? " radio--selected radio--check" : ""}`}>{isSelected && <Icon path={mdiCheck} size={0.65} color="#fff" />}</div>
        <div className="agent-card_logo">
          {agent.image ? <img src={agent.image} alt={agent.name} /> : <div className="agent-card_logo-placeholder">{agent.name?.charAt(0)}</div>}
        </div>
        <div className="agent-card_details">
          <span className="agent-card_name">{agent.name}</span>
          <span className="agent-card_address">{agent.address}</span>
        </div>
        <button className="agent-card_info-btn" onClick={e => { e.stopPropagation(); setInfoAgent(agent); }} aria-label="Agent info">
          <Icon path={mdiInformationOutline} size="22px" color="#1976D2" />
        </button>
      </div>
    );
  };

  const renderAgentCardSkeleton = (key: number) => (
    <div className="agent-card agent-card--skeleton" key={key}>
      <div className="skeleton-block skeleton-block--radio" />
      <div className="skeleton-block skeleton-block--logo" />
      <div className="agent-card_details">
        <div className="skeleton-block skeleton-block--name" />
        <div className="skeleton-block skeleton-block--address" />
      </div>
      <div className="skeleton-block skeleton-block--info" />
    </div>
  );

  const renderReasonRows = (form: any, selectedReason: string | undefined, setShowFreetext: (v: boolean) => void) => (
    <div className="reason-list">
      {agentChangeReasonOptions.map(opt => {
        const isSelected = selectedReason === opt.value;
        return (
          <div
            key={opt.value}
            className={`reason-row${isSelected ? " reason-row--selected" : ""}`}
            onClick={() => { form.setFieldValue("reason", opt.value); setShowFreetext(opt.value === "others"); }}
          >
            <div className={`radio${isSelected ? " radio--selected radio--check" : ""}`}>
              {isSelected && <Icon path={mdiCheck} size={0.65} color="#fff" />}
            </div>
            <span className="reason-row_label">{opt.label}</span>
          </div>
        );
      })}
    </div>
  );

  const renderStep3 = () => (
    <>
      {stickyLookupLoading ? (
        <div className="agent-list agent-list--loading">
          {[0, 1, 2, 3].map(renderAgentCardSkeleton)}
        </div>
      ) : isStickyMode ? (
        <>
          {stickyRequestSubmitted && (
            <div className="sticky-success-banner">
              <Icon path={mdiCheck} size="22px" color="#008353" />
              <span>{stickyRequestMessage}</span>
            </div>
          )}
          <div className="referral-agent-wrapper">
            {renderAgentCard(stickyBoundAgent as Agent, true)}
            <div className="referral-agent-banner">
              <p className="referral-agent-banner_text">
                {`You're previously assigned to ${stickyBoundAgent?.agency_full_name || stickyBoundAgent?.name || stickyBoundAgent?.username}, so this booking will be served by them.`}
              </p>
              <button className="referral-agent-banner_change-link" onClick={handleStickyChangeAgentClick}>
                {stickyRequestSubmitted ? "Check my request" : "Change Agent"}
              </button>
            </div>
          </div>
        </>
      ) : preferredAgent?.id != null ? (
        <div className="referral-agent-wrapper">
          {renderAgentCard(preferredAgent as Agent, true)}
          <div className="referral-agent-banner">
            <p className="referral-agent-banner_text">
              Your agent has been assigned based on the referral link you used.
            </p>
            <button className="referral-agent-banner_change-link" onClick={openReferralChangeModal}>
              Change Agent
            </button>
          </div>
        </div>
      ) : agentsLoading ? (
        <div className="agent-list agent-list--loading">
          {[0, 1, 2, 3].map(renderAgentCardSkeleton)}
        </div>
      ) : (
        <>
          <div className="agent-search">
            <Input prefix={<Icon path={mdiMagnify} size="18px" color="#aaa" />} placeholder="Search city, area" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} allowClear size="large" className="agent-search_input" />
          </div>
          {allStates.length > 0 && (
            <div className={`state-chips-wrapper${stateChipsExpanded ? " state-chips-wrapper--expanded" : ""}`}>
              <div className="state-chips">
                <button
                  className={`state-chip${selectedState === null ? " state-chip--active" : ""}`}
                  onClick={() => setSelectedState(null)}
                >
                  All ({agents.length})
                </button>
                {allStates.map(state => (
                  <button
                    key={state}
                    className={`state-chip${selectedState === state ? " state-chip--active" : ""}`}
                    onClick={() => setSelectedState(prev => prev === state ? null : state)}
                  >
                    {state} ({stateCounts[state]})
                  </button>
                ))}
              </div>
              {!stateChipsExpanded && <div className="state-chips-wrapper_fade" />}
              <button
                className="state-chips-wrapper_toggle"
                onClick={() => setStateChipsExpanded(v => !v)}
                aria-label={stateChipsExpanded ? "Collapse states" : "Expand states"}
              >
                <Icon path={stateChipsExpanded ? mdiChevronUp : mdiChevronDown} size="24px" color="#1E1E1E" />
              </button>
            </div>
          )}
          <div className="agent-list">
            {filteredAgents.length === 0 ? (
              <div className="agent-list_empty">No agents found</div>
            ) : filteredAgents.map(agent => renderAgentCard(agent, false, () => { setSelectedAgentId(agent.id); setDtaType(DTA_TYPE.DIRECT); }))}
          </div>
        </>
      )}

      <Drawer
        open={showReferralChangeModal}
        onClose={() => setShowReferralChangeModal(false)}
        placement="bottom"
        height="100%"
        closable={false}
        className="reason-drawer"
        rootClassName="v3"
      >
        <div className="reason-drawer_header">
          <span className="reason-drawer_title">Change Agent?</span>
          <button type="button" className="reason-drawer_close" aria-label="Close" onClick={() => setShowReferralChangeModal(false)}>
            <Icon path={mdiClose} size="24px" color="#1E1E1E" />
          </button>
        </div>
        <p className="reason-drawer_body">Please let us know why you'd like to change your assigned agent.</p>
        <Form form={changeAgentForm} layout="vertical" onFinish={submitReferralChangeAgent} className="reason-form">
          {renderReasonRows(changeAgentForm, referralReasonValue, setShowReferralReasonFreetext)}
          <Form.Item name="reason" hidden rules={[{ required: true, message: "Please select a reason" }]}>
            <Input />
          </Form.Item>
          {showReferralReasonFreetext && (
            <Form.Item name="reason_other" className="reason-other-input" rules={[{ required: true, message: "Please specify your reason" }]}>
              <Input.TextArea placeholder="Share your reason" rows={4} />
            </Form.Item>
          )}
          <div className="reason-drawer_footer">
            <Button type="primary" htmlType="submit" size="large" block>Submit</Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        open={showStickyConfirmModal}
        onCancel={() => setShowStickyConfirmModal(false)}
        footer={null}
        centered
        className="change-agent-modal"
        rootClassName="v3"
      >
        <h3 className="change-agent-modal_title">Change Agent?</h3>
        <p className="change-agent-modal_body">
          Once you change your agent, you’ll need to select another agent to continue with this booking.
        </p>
        <button type="button" className="change-agent-modal_confirm" onClick={() => { setShowStickyConfirmModal(false); setShowStickyChangeModal(true); }}>Change Agent</button>
        <button type="button" className="change-agent-modal_cancel" onClick={() => setShowStickyConfirmModal(false)}>Cancel</button>
      </Modal>

      <Modal
        open={showSignInRequiredModal}
        onCancel={() => setShowSignInRequiredModal(false)}
        footer={null}
        centered
        destroyOnClose
        className="change-agent-modal"
        rootClassName="v3"
      >
        <h3 className="change-agent-modal_title">Sign In Required</h3>
        <p className="change-agent-modal_body">Please sign in to request a change of your current agent assignment</p>
        <SignInForm onSuccess={() => {
          setShowSignInRequiredModal(false);
          setStickyLookupLoading(true);
          setPendingStickyResume(true);
        }} />
        <button type="button" className="change-agent-modal_cancel" onClick={() => setShowSignInRequiredModal(false)}>Cancel</button>
      </Modal>

      <Drawer
        open={showStickyChangeModal}
        onClose={() => setShowStickyChangeModal(false)}
        placement="bottom"
        height="100%"
        closable={false}
        className="reason-drawer"
        rootClassName="v3"
      >
        <div className="reason-drawer_header">
          <span className="reason-drawer_title">Request to release my agent assignment</span>
          <button type="button" className="reason-drawer_close" aria-label="Close" onClick={() => setShowStickyChangeModal(false)}>
            <Icon path={mdiClose} size="24px" color="#1E1E1E" />
          </button>
        </div>
        <div className="reason-drawer_info-banner">
          <div className="reason-drawer_info-icon">
            <Icon path={mdiInformationOutline} size="22px" color="#0b2b3e" />
          </div>
          <span>
            {`Your current booking will still be served by ${stickyBoundAgent?.agency_full_name || stickyBoundAgent?.name}. After the cooldown period, you'll be released from this assignment and free to book with a different agent on your next booking.`}
          </span>
        </div>
        <Form form={stickyChangeForm} layout="vertical" onFinish={submitStickyChangeRequest} className="reason-form">
          {renderReasonRows(stickyChangeForm, stickyReasonValue, setShowStickyReasonFreetext)}
          <Form.Item name="reason" hidden rules={[{ required: true, message: "Please select a reason" }]}>
            <Input />
          </Form.Item>
          {showStickyReasonFreetext && (
            <Form.Item name="reason_other" className="reason-other-input" rules={[{ required: true, message: "Please specify your reason" }]}>
              <Input.TextArea placeholder="Share your reason" rows={4} />
            </Form.Item>
          )}
          <div className="reason-drawer_footer">
            <Button type="primary" htmlType="submit" size="large" block loading={stickyRequestSubmitting}>
              {stickyRequestSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Drawer title="Agent Info" placement="bottom" height="80%" open={infoAgent !== null} onClose={() => setInfoAgent(null)} rootClassName="v3 agent-info-drawer">
        {infoAgent && (
          <div className="agent-info">
            <div className="agent-info_identity">
              <div className="agent-info_logo-wrap">
                {infoAgent.image
                  ? <img src={infoAgent.image} alt={infoAgent.name} className="agent-info_logo" />
                  : <div className="agent-info_logo-placeholder" />
                }
              </div>
              <span className="agent-info_name">{infoAgent.name}</span>
            </div>
            <div className="agent-info_address-row">
              <div className="agent-info_address-text">
                <span className="agent-info_meta-label">{infoAgent.name}</span>
                <span className="agent-info_address">{infoAgent.address}</span>
              </div>
              <a
                className="agent-info_location-btn"
                href={`https://maps.google.com/?q=${encodeURIComponent(infoAgent.address)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon path={mdiNavigation} size={0.8} color="#dc1a22" rotate={45} />
              </a>
            </div>
            {(infoAgent.whatsapp || infoAgent.phone || infoAgent.email) && (
              <div className="agent-info_card">
                <span className="agent-info_card-title">Agency Contact</span>
                <div className="agent-info_contacts">
                  {infoAgent.whatsapp && (
                    <div className="agent-info_contact-row">
                      <div className="agent-info_contact-text">
                        <span className="agent-info_contact-label">WhatsApp</span>
                        <span className="agent-info_contact-value">{infoAgent.whatsapp}</span>
                      </div>
                      <a href={`https://wa.me/${infoAgent.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="agent-info_icon-btn agent-info_icon-btn--whatsapp">
                        <Icon path={mdiWhatsapp} size={0.9} color="#fff" />
                      </a>
                    </div>
                  )}
                  {infoAgent.phone && (
                    <div className="agent-info_contact-row">
                      <div className="agent-info_contact-text">
                        <span className="agent-info_contact-label">Phone</span>
                        <span className="agent-info_contact-value">{infoAgent.phone}</span>
                      </div>
                      <a href={`tel:${infoAgent.phone}`} className="agent-info_icon-btn agent-info_icon-btn--phone">
                        <Icon path={mdiPhone} size={0.9} color="#dc1a22" />
                      </a>
                    </div>
                  )}
                  {infoAgent.email && (
                    <div className="agent-info_contact-row">
                      <div className="agent-info_contact-text">
                        <span className="agent-info_contact-label">Email</span>
                        <span className="agent-info_contact-value">{infoAgent.email}</span>
                      </div>
                      <a href={`mailto:${infoAgent.email}`} className="agent-info_icon-btn agent-info_icon-btn--email">
                        <Icon path={mdiEmail} size={0.9} color="#dc1a22" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );

  const renderStep4 = () => (
    <>
      <div className="summary-card">
        <div className="summary-card_tour-wrap">
          <h3 ref={summaryTourRef} className={`summary-card_tour${summaryTourExpanded ? " expanded" : ""}`}>{itinerary.caption}</h3>
          {summaryTourClamped && (
            <button type="button" className="summary-card_readmore" onClick={() => setSummaryTourExpanded(v => !v)}>
              {summaryTourExpanded ? "Show Less" : "Read More"}
            </button>
          )}
        </div>

        <div className="summary-row" onClick={() => setCurrentStep(1)}>
          <div className="summary-row_left">
            <img src="/iceholidays-assets/images/v3/icon-calendar.svg" width={20} height={20} aria-hidden="true" />
            <div className="summary-row_text">
              <span className="summary-row_label">Departure Date</span>
              <span className="summary-row_value">{safeFormat(localSelectedTour.departure_date)}</span>
            </div>
          </div>
          <Icon path={mdiChevronRight} size="24px" color="#33343D" />
        </div>

        <div className="summary-divider" />

        <div className="summary-row" onClick={() => setCurrentStep(2)}>
          <div className="summary-row_left">
            <img src="/iceholidays-assets/images/v3/icon-travellers.svg" width={20} height={20} aria-hidden="true" />
            <div className="summary-row_text">
              <span className="summary-row_label">Traveller Details</span>
              <span className="summary-row_value">{travellerSummary || "—"}</span>
            </div>
          </div>
          <Icon path={mdiChevronRight} size="24px" color="#33343D" />
        </div>

        <div className="summary-divider" />

        <div className="summary-row" onClick={() => setCurrentStep(3)}>
          <div className="summary-row_left">
            <img src="/iceholidays-assets/images/v3/icon-agent.svg" width={20} height={20} aria-hidden="true" />
            <div className="summary-row_text">
              <span className="summary-row_label">Preferred Partner</span>
              <span className="summary-row_value">{selectedAgent?.name || "—"}</span>
            </div>
          </div>
          <Icon path={mdiChevronRight} size="24px" color="#33343D" />
        </div>

      </div>

      <div className="einvoice-card">
        <div className="einvoice-card_header">
          <span className="einvoice-card_title">e-Invoice Address</span>
          <button className="einvoice-card_edit-btn" onClick={() => { addressForm.setFieldsValue(addressData); setAddressDrawerOpen(true); }}>Edit</button>
        </div>
        {hasAddress ? (
          <div className="einvoice-card_preview">
            <span>{[addressData.address1, addressData.address2].filter(Boolean).join(", ")}</span>
            <span>{[addressData.city, addressData.state, addressData.postcode, addressData.country].filter(Boolean).join(", ")}</span>
          </div>
        ) : (
          <p className="einvoice-card_subtitle">Please fill this in if you require an e-Invoice.</p>
        )}
      </div>

      <Drawer
        title="e-Invoice Address"
        placement="bottom"
        height="100%"
        open={addressDrawerOpen}
        onClose={() => setAddressDrawerOpen(false)}
        rootClassName="v3 address-drawer"
        footer={<Button type="primary" block onClick={saveAddress} className="address-drawer_done-btn">Done</Button>}
      >
        <p className="address-drawer_subtitle">Please fill this in if you require an e-Invoice.</p>
        <Form form={addressForm} layout="vertical" className="address-form">
          <Form.Item label="Address 1" name="address1" dependencies={["city","state","postcode","country"]} rules={[({ getFieldValue }) => ({ validator(_, value) { const others = ["state","city","postcode","country"].some(f => getFieldValue(f)); if (others && (!value || !value.trim())) return Promise.reject(new Error("This field cannot be empty")); return Promise.resolve(); } })]}>
            <Input placeholder="e.g. No 12, Jalan Ampang" />
          </Form.Item>
          <Form.Item label="Address 2" name="address2">
            <Input placeholder="Apartment, suite, unit, block, floor, taman, kampung, etc. (Optional)" />
          </Form.Item>
          <Form.Item label="State" name="state" dependencies={["address1","city","postcode","country"]} rules={[({ getFieldValue }) => ({ validator(_, value) { const others = ["address1","city","postcode","country"].some(f => getFieldValue(f)); if (others && !value) return Promise.reject(new Error("This field cannot be empty")); return Promise.resolve(); } })]}>
            <Select showSearch allowClear placeholder="Please Select" options={filteredStates} onChange={filterCitiesByState} />
          </Form.Item>
          <Form.Item label="City" name="city" dependencies={["address1","state","postcode","country"]} rules={[({ getFieldValue }) => ({ validator(_, value) { const others = ["address1","state","postcode","country"].some(f => getFieldValue(f)); if (others && !value) return Promise.reject(new Error("This field cannot be empty")); return Promise.resolve(); } })]}>
            <Select showSearch allowClear placeholder="Please Select" options={filteredCities} onChange={filterStatesByCity} />
          </Form.Item>
          <Form.Item label="Postcode" name="postcode" dependencies={["address1","state","city","country"]} rules={[({ getFieldValue }) => ({ validator(_, value) { const others = ["address1","state","city","country"].some(f => getFieldValue(f)); if (others && (!value || !value.trim())) return Promise.reject(new Error("This field cannot be empty")); return Promise.resolve(); } })]}>
            <Input placeholder="e.g. 55100" />
          </Form.Item>
          <Form.Item label="Country" name="country" dependencies={["address1","state","city","postcode"]} rules={[({ getFieldValue }) => ({ validator(_, value) { const others = ["address1","state","city","postcode"].some(f => getFieldValue(f)); if (others && !value) return Promise.reject(new Error("This field cannot be empty")); return Promise.resolve(); } })]}>
            <Select showSearch allowClear placeholder="Please Select" options={filteredCountries.length ? filteredCountries : countries} onChange={changeCityByCountry} />
          </Form.Item>
        </Form>
      </Drawer>

      <div className="form-section">
        <h3 className="form-section_title">Select Payment Terms</h3>

        <div
          className={`terms-card${fareType === PAYMENT_TERMS.FULL_PAYMENT ? " terms-card--selected" : ""}`}
          onClick={() => setFareType(PAYMENT_TERMS.FULL_PAYMENT)}
        >
          <div className={`radio${fareType === PAYMENT_TERMS.FULL_PAYMENT ? " radio--selected radio--check" : ""}`}>
            {fareType === PAYMENT_TERMS.FULL_PAYMENT && <Icon path={mdiCheck} size={0.65} color="#fff" />}
          </div>
          <div className="terms-card_body">
            <span className="terms-card_name">Pay in Full</span>
            <span className="terms-card_desc">One-time complete payment, nothing more to pay</span>
            <span className="terms-card_amount">{currencyDisplay(itinerary.priceCurrency, grandTotal)}</span>
          </div>
        </div>

        {canPayDeposit && (
          <div
            className={`terms-card${fareType === PAYMENT_TERMS.DEPOSIT ? " terms-card--selected" : ""}`}
            onClick={() => setFareType(PAYMENT_TERMS.DEPOSIT)}
          >
            <div className={`radio${fareType === PAYMENT_TERMS.DEPOSIT ? " radio--selected radio--check" : ""}`}>
              {fareType === PAYMENT_TERMS.DEPOSIT && <Icon path={mdiCheck} size={0.65} color="#fff" />}
            </div>
            <div className="terms-card_body">
              <span className="terms-card_name">Pay booking fee first</span>
              <span className="terms-card_amount">{currencyDisplay(itinerary.priceCurrency, depositGrandTotal)}</span>
              {localSelectedTour.final_payment_date && (
                <div className="terms-card_notice">
                  Final payment of <strong>{currencyDisplay(itinerary.priceCurrency, remainingBalance)}</strong> is due<br />
                  before {safeFormat(localSelectedTour.final_payment_date)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3 className="form-section_title">Select Payment Method</h3>
        <div className="payment-channels">
          {PAYMENT_CHANNELS.map(channel => {
            const isActive = selectedChannel?.value === channel.value;
            return (
              <div
                key={channel.id}
                className={`channel-row${isActive ? " channel-row--selected" : ""}`}
                onClick={() => { setSelectedChannel(channel); setSelectedBank(""); }}
              >
                <img
                  src={`/iceholidays-assets/images/payment/${channel.logo}`}
                  alt={channel.label}
                  className="channel-logo"
                />
                <span className="channel-label">{channel.label}</span>
                <div className={`radio${isActive ? " radio--selected" : ""}`}>
                  {isActive && <div className="radio_dot" />}
                </div>
              </div>
            );
          })}
        </div>

        {needsBankSelection && (
          <>
            <div className="bank-select-trigger" onClick={() => setBankDrawerOpen(true)}>
              {selectedBank
                ? <span className="bank-select-trigger_value">{bankOptions.find(b => b.value === selectedBank)?.label}</span>
                : <span className="bank-select-trigger_placeholder">Select {selectedChannel!.fieldLabel}</span>
              }
              <Icon path={mdiChevronRight} size="20px" color="#888" />
            </div>

            <Drawer
              title="Choose your bank"
              placement="bottom"
              height="80%"
              open={bankDrawerOpen}
              onClose={() => setBankDrawerOpen(false)}
              rootClassName="v3 bank-select-drawer"
              footer={
                <Button type="primary" block onClick={() => setBankDrawerOpen(false)} className="bank-select-drawer_done-btn">
                  Done
                </Button>
              }
            >
              <div className="bank-list">
                {bankOptions.map((bank, idx) => (
                  <React.Fragment key={bank.value}>
                    <div
                      className={`bank-row${selectedBank === bank.value ? " bank-row--selected" : ""}${bank.disabled ? " bank-row--disabled" : ""}`}
                      onClick={() => { if (!bank.disabled) setSelectedBank(bank.value); }}
                    >
                      <span className="bank-row_name">{bank.label}</span>
                      <div className={`radio${selectedBank === bank.value ? " radio--selected radio--check" : ""}`}>
                        {selectedBank === bank.value && <Icon path={mdiCheck} size={0.65} color="#fff" />}
                      </div>
                    </div>
                    {idx < bankOptions.length - 1 && <div className="bank-row_divider" />}
                  </React.Fragment>
                ))}
              </div>
            </Drawer>
          </>
        )}
      </div>

      <div className="form-section agreement">
        <h3 className="form-section_title">Agreement</h3>
        <div className="consent-card">
          <TermsConsentCheckBox
            checked={termsAgreed}
            onCheckedChange={setTermsAgreed}
          />
          <PrivacyConsentCheckbox
            checked={privacyAgreed}
            onCheckedChange={setPrivacyAgreed}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={`v3 booking-form${currentStep === 3 ? " booking-form--step3" : ""}`}>
      <div className="booking-form_header">
        <Button type="text" className="booking-form_back-btn" icon={<Icon path={mdiChevronLeft} size="28px" />} onClick={handleBack} />
        <div className="booking-form_progress">
          {STEPS.map(s => (
            <div key={s.step} className={`progress-segment${s.step <= currentStep ? " active" : ""}`} />
          ))}
        </div>
      </div>

      {checkoutDisabled && (
        <div className="v3-checkout-disabled-banner">
          <Icon path={mdiInformationOutline} size={1} />
          <span>{checkoutMessage}</span>
        </div>
      )}
      <div className="booking-form_content">
        { currentStepData?.title !== '' && (
          <h2 className="booking-form_title">{currentStepData.title}</h2>
        )}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Sticky bottom bar — shown on every step so the running price and its
          breakdown stay visible; only the CTA changes between steps. */}
      <div className="booking-form_bottom-bar">
        <div className="booking-form_summary">
          <span className="booking-form_summary-date">
            Departure Date: {safeFormat(departureDate)}
          </span>
          {hasPriceBreakdown ? (
            <span
              className="bottom-price bottom-price--clickable"
              role="button"
              tabIndex={0}
              onClick={() => setShowPriceSummary(true)}
              onKeyDown={(e) => { if (e.key === "Enter") setShowPriceSummary(true); }}
            >
              {promotionCalcLoading ? <Spin size="small" /> : displayAmount}
              <Icon path={mdiChevronDown} color={'#5D5F6A'} size="20px" />
            </span>
          ) : (
            <span className="bottom-price">
              {promotionCalcLoading ? <Spin size="small" /> : displayAmount}
            </span>
          )}
          <small className="bottom-note">(incl. taxes &amp; fees)</small>
        </div>
        {isLastStep ? (
          <Button
            type="primary"
            size="large"
            className="booking-form_submit-btn"
            disabled={checkoutDisabled || !canProceedStep4 || promotionCalcLoading || cruiseTourDetailLoading}
            loading={cruiseTourDetailLoading}
            onClick={handlePayment}
          >
            Make Payment
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            className="booking-form_submit-btn"
            disabled={checkoutDisabled || promotionCalcLoading || (currentStep === 3 && (!selectedAgentId || stickyLookupLoading)) || cruiseTourDetailLoading}
            loading={(currentStep === 3 && stickyLookupLoading) || cruiseTourDetailLoading}
            onClick={handleNext}
          >
            {hasCabinNavigation && currentStep === 2 ? "Done" : "Next"}
          </Button>
        )}
      </div>

      {hasCabinNavigation && (
        <Drawer
          title="Travellers"
          placement="bottom"
          height="100%"
          open={travellersDrawerOpen}
          onClose={() => setTravellersDrawerOpen(false)}
          closeIcon={<Icon path={mdiClose} size="24px" />}
          rootClassName="v3 cruise-travellers-drawer"
          footer={
            <div className="cruise-travellers-drawer_footer">
              <div className="booking-form_summary">
                <span className="booking-form_summary-date">
                  Departure Date: {safeFormat(departureDate)}
                </span>
                {hasPriceBreakdown ? (
                  <span
                    className="bottom-price bottom-price--clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowPriceSummary(true)}
                    onKeyDown={(e) => { if (e.key === "Enter") setShowPriceSummary(true); }}
                  >
                    {displayAmount}
                    <Icon path={mdiChevronDown} color={'#5D5F6A'} size="20px" />
                  </span>
                ) : (
                  <span className="bottom-price">{displayAmount}</span>
                )}
                <small className="bottom-note">(incl. taxes &amp; fees)</small>
              </div>
              <Button
                type="primary"
                size="large"
                className="booking-form_submit-btn"
                disabled={cruisePaxTotal === 0}
                onClick={() => {
                  setTravellersDrawerOpen(false);
                  if (travellersConfirmed) return;
                  setTravellersConfirmed(true);
                  setCurrentStep(2);
                }}
              >
                {travellersConfirmed ? "Update Selection" : "Done"}
              </Button>
            </div>
          }
        >
          <CruiseTravelersV3
            cabins={cruiseCabins}
            setCabins={setCruiseCabins ?? (() => {})}
            multiplier={cruiseMultiplier || 0}
            maxCabins={maxCruiseCabins}
            childAgeRanges={itinerary.child_age_ranges}
          />
        </Drawer>
      )}

      {/* Rendered via portal to <body> so it escapes the booking Drawer's
          transformed content-wrapper (which would otherwise trap fixed
          positioning) and sits above the full-screen booking flow. */}
      {showPriceSummary && createPortal(
        <div className="v3 price-summary-sheet-root">
          <div
            className="price-summary-sheet_backdrop"
            onClick={() => setShowPriceSummary(false)}
          />
          <div className="price-summary-sheet" role="dialog" aria-label="Price Summary">
            <div className="price-summary-sheet_header">
              <span className="section-label">Price Summary</span>
              <button
                type="button"
                className="price-summary-sheet_close"
                aria-label="Close"
                onClick={() => setShowPriceSummary(false)}
              >
                <Icon path={mdiClose} size="24px" />
              </button>
            </div>
            <div className="price-summary">
              {priceBreakdown.map(({ key, label, count, sum, tiers }) => (
                tiers && tiers.length > 0 ? (
                  tiers.map((tier, tierIndex) => (
                    <div key={`${key}-${tierIndex}`} className="price-summary_row">
                      <span className="price-summary_label">{priceTypeLabel(tier.price_type)} {label} x {tier.count}</span>
                      <span className="price-summary_value">
                        {currencyDisplay(itinerary.priceCurrency, tier.sum)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div key={key} className="price-summary_row">
                    <span className="price-summary_label">{label} x {count}</span>
                    <span className="price-summary_value">
                      {currencyDisplay(itinerary.priceCurrency, sum)}
                    </span>
                  </div>
                )
              ))}
              {bookingAddons.map((addon) => (
                <div key={addon.label} className="price-summary_row">
                  <span className="price-summary_label">{addon.label}</span>
                  <span className="price-summary_value">
                    {addon.totalPax} x {currencyDisplay(itinerary.priceCurrency, addon.perPaxValue)}
                  </span>
                </div>
              ))}
              {nonMalaysiaChargeAmount > 0 && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Non-Malaysian Charge</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, nonMalaysiaChargeAmount)}
                  </span>
                </div>
              )}
              {visaDeduction > 0 && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Visa Fee</span>
                  <span className="price-summary_value">
                    - {currencyDisplay(itinerary.priceCurrency, visaDeduction)}
                  </span>
                </div>
              )}
              {!promotionCalcLoading && promotionDeductions.map((promo) => (
                <div key={promo.id} className="price-summary_row">
                  <span className="price-summary_label">{promo.title} x {promo.entitledPaxCount}</span>
                  <span className="price-summary_value">
                    - {currencyDisplay(itinerary.priceCurrency, promo.amount)}
                  </span>
                </div>
              ))}
              {showAdminFee && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Admin Fee</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, adminFeeShown)}
                  </span>
                </div>
              )}
              <div className="price-summary_divider" />
              <div className="price-summary_row price-summary_row--total">
                <span className="price-summary_label">
                  {fareType === PAYMENT_TERMS.DEPOSIT ? "Total Due Now" : "Grand Total"}
                </span>
                <span className="price-summary_value">{promotionCalcLoading ? <Spin size="small" /> : displayAmount}</span>
              </div>
              {fareType === PAYMENT_TERMS.DEPOSIT && (
                <div className="price-summary_row">
                  <span className="price-summary_label">Remaining Balance</span>
                  <span className="price-summary_value">
                    {currencyDisplay(itinerary.priceCurrency, remainingBalance)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      {isSubmitting && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-overlay_card">
            <Spin size="large" />
            <span className="payment-processing-overlay_text">Processing payment...</span>
            <span className="payment-processing-overlay_sub">Please do not close this page</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFormV3;
